import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { authService } from '../../../services/authService';
import toast from 'react-hot-toast';
import learningLevelsData from '../../../data/learningLevels.json';
import { getCountryTranslation } from '../../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../../services/departmentsTranslationsService';
import { 
  UsersIcon, 
  SearchIcon, 
  PlusIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  UserCheckIcon,
  UserXIcon,
  BanIcon,
  UnlockIcon,
  KeyIcon,
  SettingsIcon,
  GraduationCapIcon,
  BuildingIcon,
  FileSpreadsheetIcon,
  DownloadIcon,
  LayersIcon,
  LockIcon,
  MessageCircleIcon
} from 'lucide-react';
import * as XLSX from 'xlsx';

/** رقم دولي بدون + لاستخدام wa.me (دعم بسيط لأرقام MA تبدأ بـ 0) */
function normalizePhoneForWhatsApp(phone) {
  if (!phone) return '';
  let d = String(phone).replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('212')) return d;
  if (d.length === 10 && d.startsWith('0')) {
    return `212${d.slice(1)}`;
  }
  return d;
}

// استخراج هيكل تنظيمي (معرفات) من عنصر organizationalAccess
const normalizeOrgScope = (access) => {
  if (!access || typeof access !== 'object') return null;
  const id = (key) => access[key] ?? access[key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')];
  const countryId = id('countryId') ?? id('country_id');
  const regionId = id('regionId') ?? id('region_id');
  const academyId = id('academyId') ?? id('academy_id');
  const districtId = id('districtId') ?? id('district_id');
  const institutionId = id('institutionId') ?? id('institution_id');
  const departmentId = id('departmentId') ?? id('department_id');
  const scope = {
    countryId: countryId != null && countryId !== '' ? Number(countryId) : '',
    regionId: regionId != null && regionId !== '' ? Number(regionId) : '',
    academyId: academyId != null && academyId !== '' ? Number(academyId) : '',
    districtId: districtId != null && districtId !== '' ? Number(districtId) : '',
    institutionId: institutionId != null && institutionId !== '' ? Number(institutionId) : '',
    departmentId: departmentId != null && departmentId !== '' ? Number(departmentId) : ''
  };
  const hasAny = Object.values(scope).some((v) => v !== '' && v != null);
  return hasAny ? scope : null;
};

// استخراج أسماء الهيكل من organizationalAccess (لعرض المستويات المقفلة بدون استدعاء API)
const normalizeOrgScopeNames = (access) => {
  if (!access || typeof access !== 'object') return null;
  const n = (camel, snake) => access[camel] ?? access[snake] ?? '';
  return {
    countryName: n('countryName', 'country_name'),
    regionName: n('regionName', 'region_name'),
    academyName: n('academyName', 'academy_name'),
    districtName: n('districtName', 'district_name'),
    institutionName: n('institutionName', 'institution_name'),
    departmentName: n('departmentName', 'department_name')
  };
};

const Users = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { user: currentUser, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canReadOrganization = hasPermission?.('organization.read') ?? true;

  // هيكل المستخدم الحالي (لتعيين المستخدمين تحت إشرافه) — يُملأ عند فتح مودال التعيين
  const [orgScopeFromCurrentUser, setOrgScopeFromCurrentUser] = useState(null);
  const [orgScopeNamesFromCurrentUser, setOrgScopeNamesFromCurrentUser] = useState(null);
  
  // State management
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [filters, setFilters] = useState({
    roleId: '',
    departmentId: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  
  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    phone: '',
    dateOfBirth: '',
    gender: 'other'
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState({
    id: '',
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    gender: 'other',
    username: '',
    email: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Role assignment modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [isAssigningRole, setIsAssigningRole] = useState(false);
  const [removingRoleId, setRemovingRoleId] = useState(null);

  // Student account creation modal state
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedUserForStudent, setSelectedUserForStudent] = useState(null);
  const [learningLevels, setLearningLevels] = useState([]);
  const [learningLevelsLoading, setLearningLevelsLoading] = useState(false);
  const [isCreatingStudent, setIsCreatingStudent] = useState(false);
  const [studentFormData, setStudentFormData] = useState({
    userId: '',
    studentNumber: '',
    currentLevelId: '',
    enrollmentDate: ''
  });
  const [studentFormErrors, setStudentFormErrors] = useState({});
  
  // Student account verification state
  const [studentAccountStatus, setStudentAccountStatus] = useState({});
  const [checkingStudentStatus, setCheckingStudentStatus] = useState({});

  // Bulk actions state (selection applies to all bulk actions)
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkStudentModal, setShowBulkStudentModal] = useState(false);
  const [bulkStudentFormData, setBulkStudentFormData] = useState({
    currentLevelId: '',
    enrollmentDate: '',
    studentNumberPrefix: 'STU'
  });
  const [bulkStudentFormErrors, setBulkStudentFormErrors] = useState({});
  const [isCreatingBulkStudents, setIsCreatingBulkStudents] = useState(false);
  const [bulkStudentProgress, setBulkStudentProgress] = useState({ current: 0, total: 0 });
  const [bulkStudentResults, setBulkStudentResults] = useState({ success: 0, failed: 0, errors: [] });

  // Bulk organization assignment state
  const [showBulkOrgModal, setShowBulkOrgModal] = useState(false);
  const [bulkOrgFormData, setBulkOrgFormData] = useState({
    countryId: '',
    regionId: '',
    academyId: '',
    districtId: '',
    institutionId: '',
    departmentId: ''
  });
  const [bulkOrgFormErrors, setBulkOrgFormErrors] = useState({});
  const [isAssigningBulkOrg, setIsAssigningBulkOrg] = useState(false);
  const [bulkOrgProgress, setBulkOrgProgress] = useState({ current: 0, total: 0 });
  const [bulkOrgResults, setBulkOrgResults] = useState({ success: 0, failed: 0, errors: [] });
  const [loadingBulkOrgScope, setLoadingBulkOrgScope] = useState(false);

  // Bulk status (verification) change state
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState(true); // true = verified, false = unverified
  const [isUpdatingBulkStatus, setIsUpdatingBulkStatus] = useState(false);
  const [bulkStatusProgress, setBulkStatusProgress] = useState({ current: 0, total: 0 });
  const [bulkStatusResults, setBulkStatusResults] = useState({ success: 0, failed: 0, errors: [] });

  // Organizational assignment modal state
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [selectedUserForOrg, setSelectedUserForOrg] = useState(null);
  const [isAssigningOrg, setIsAssigningOrg] = useState(false);
  const [orgFormData, setOrgFormData] = useState({
    userId: '',
    countryId: '',
    regionId: '',
    academyId: '',
    districtId: '',
    institutionId: '',
    departmentId: ''
  });
  const [orgFormErrors, setOrgFormErrors] = useState({});
  const [loadingTargetUserOrg, setLoadingTargetUserOrg] = useState(false);

  // Organizational data state
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(false);

  // Trainer departments assignment state (for trainer role)
  const [showTrainerDeptsModal, setShowTrainerDeptsModal] = useState(false);
  const [selectedUserForTrainerDepts, setSelectedUserForTrainerDepts] = useState(null);
  const [trainerAssignments, setTrainerAssignments] = useState([]);
  const [trainerAssignmentsLoading, setTrainerAssignmentsLoading] = useState(false);
  const [trainerDeptFormData, setTrainerDeptFormData] = useState({
    countryId: '',
    regionId: '',
    academyId: '',
    districtId: '',
    institutionId: '',
    departmentIds: []
  });
  const [trainerDeptFormErrors, setTrainerDeptFormErrors] = useState({});
  const [isAssigningTrainerDepts, setIsAssigningTrainerDepts] = useState(false);
  const [removingAssignmentId, setRemovingAssignmentId] = useState(null);

  // Excel import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, errors: [] });
  const fileInputRef = React.useRef(null);

  // Change password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const passwordModalWhatsAppPhone =
    showPasswordModal && passwordUser ? normalizePhoneForWhatsApp(passwordUser.phone) : '';
  const canSendPasswordWhatsApp = Boolean(
    passwordModalWhatsAppPhone && String(passwordModalWhatsAppPhone).length >= 8
  );

  // Fetch users
  const fetchUsers = async (page = 1, search = '', filters = {}) => {
    setLoading(true);
    try {
      let url = `/users?page=${page}&limit=${limit}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      if (filters.roleId) {
        url += `&role=${filters.roleId}`;
      }
      
      if (filters.departmentId) {
        url += `&departmentId=${encodeURIComponent(filters.departmentId)}`;
      }
      

      const response = await api.get(url);
      
      if (response.data.success) {
        setUsers(response.data.data.users || []);
        setTotal(response.data.data.pagination.total || 0);
        setCurrentPage(response.data.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('users.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await api.get('/users/roles/available');
      if (response.data.success) {
        setRoles(response.data.data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error(t('users.rolesFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setRolesLoading(false);
    }
  };

  // Fetch learning levels
  const fetchLearningLevels = async () => {
    setLearningLevelsLoading(true);
    try {
      const response = await api.get('/organization/learning-levels?page=1&limit=100');
      if (response.data.success) {
        // معالجة البيانات لإضافة الترجمات من ملف learningLevels.json
        const levelsWithTranslations = response.data.data.learningLevels.map(level => {
          const levelKey = level.name; // "المستوى الأول", "المستوى الثاني", etc.
          const translations = learningLevelsData[levelKey] || {};
          
          return {
            ...level,
            translatedName: translations[currentLanguage] || level.name,
            originalName: level.name
          };
        });
        
        setLearningLevels(levelsWithTranslations);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
      toast.error(t('users.learningLevelsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLearningLevelsLoading(false);
    }
  };

  // Fetch organizational data
  const fetchOrganizationalData = async () => {
    setLoadingOrgData(true);
    try {
      // Fetch countries
      const countriesResponse = await api.get('/organization/countries?page=1&limit=100');
      if (countriesResponse.data.success) {
        setCountries(countriesResponse.data.data.countries || []);
      }
    } catch (error) {
      console.error('Error fetching organizational data:', error);
      toast.error(t('users.orgDataFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoadingOrgData(false);
    }
  };

  // حل أسماء الهيكل إلى معرفات وتحميل القوائم المتسلسلة (عندما يعيد الـ API أسماء فقط) لتفعيل المستويات التحتية للتعديل
  // إن وُجد formSetter تُحدَّث النموذج، وتُرجع الدالة كائن المعرفات المحلولة أو null
  const resolveOrgNamesToIdsAndLoadLists = async (scopeNames, formSetter) => {
    if (!scopeNames) return null;
    const setForm = typeof formSetter === 'function' ? formSetter : null;
    const matchName = (item, name, translated) => !name ? false : (item?.name === name || translated === name);
    let resolved = { countryId: '', regionId: '', academyId: '', districtId: '', institutionId: '', departmentId: '' };
    try {
      const countriesRes = await api.get('/organization/countries?page=1&limit=100').catch(() => ({}));
      const countries = countriesRes?.data?.data?.countries || [];
      setCountries(countries);
      const countryName = scopeNames.countryName;
      const country = countryName ? countries.find((c) => matchName(c, countryName, getCountryTranslation(c?.name, currentLanguage))) : null;
      if (country) {
        resolved.countryId = country.id;
        if (setForm) setForm((prev) => ({ ...prev, countryId: country.id }));
        const regionsRes = await api.get(`/organization/countries/${country.id}/regions?page=1&limit=100`).catch(() => ({}));
        const regions = regionsRes?.data?.data?.regions || [];
        setRegions(regions);
        const regionName = scopeNames.regionName;
        const region = regionName ? regions.find((r) => matchName(r, regionName, getRegionTranslation(r?.name, currentLanguage))) : null;
        if (region) {
          resolved.regionId = region.id;
          if (setForm) setForm((prev) => ({ ...prev, regionId: region.id }));
          const academiesRes = await api.get(`/organization/regions/${region.id}/academies?page=1&limit=100`).catch(() => ({}));
          const academies = academiesRes?.data?.data?.academies || [];
          setAcademies(academies);
          const academyName = scopeNames.academyName;
          const academy = academyName ? academies.find((a) => matchName(a, academyName, getAcademyTranslation(a?.name, currentLanguage))) : null;
          if (academy) {
            resolved.academyId = academy.id;
            if (setForm) setForm((prev) => ({ ...prev, academyId: academy.id }));
            const districtsRes = await api.get(`/organization/academies/${academy.id}/districts?page=1&limit=100`).catch(() => ({}));
            const districts = districtsRes?.data?.data?.districts || [];
            setDistricts(districts);
            const districtName = scopeNames.districtName;
            const district = districtName ? districts.find((d) => matchName(d, districtName, getDistrictTranslation(d?.name, currentLanguage))) : null;
            if (district) {
              resolved.districtId = district.id;
              if (setForm) setForm((prev) => ({ ...prev, districtId: district.id }));
              const institutionsRes = await api.get(`/organization/districts/${district.id}/institutions?page=1&limit=100`).catch(() => ({}));
              const institutions = institutionsRes?.data?.data?.institutions || [];
              setInstitutions(institutions);
              const institutionName = scopeNames.institutionName;
              const institution = institutionName ? institutions.find((i) => matchName(i, institutionName, getInstitutionTranslation(i?.name, currentLanguage))) : null;
              if (institution) {
                resolved.institutionId = institution.id;
                if (setForm) setForm((prev) => ({ ...prev, institutionId: institution.id }));
                const deptsRes = await api.get(`/organization/institutions/${institution.id}/departments?page=1&limit=100`).catch(() => ({}));
                const depts = deptsRes?.data?.data?.departments || [];
                setDepartments(depts);
                const departmentName = scopeNames.departmentName;
                const department = departmentName ? depts.find((d) => matchName(d, departmentName, getDepartmentTranslation(d?.name, currentLanguage))) : null;
                if (department) {
                  resolved.departmentId = department.id;
                  if (setForm) setForm((prev) => ({ ...prev, departmentId: department.id }));
                }
              }
            }
          }
        }
      }
      return resolved;
    } catch (e) {
      console.error('resolveOrgNamesToIdsAndLoadLists', e);
      return null;
    }
  };

  // Fetch regions by country
  const fetchRegionsByCountry = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/countries/${countryId}/regions?page=1&limit=100`);
      if (response.data.success) {
        setRegions(response.data.data.regions || []);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error(t('users.regionsFetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch academies by region
  const fetchAcademiesByRegion = async (regionId) => {
    if (!regionId) {
      setAcademies([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/regions/${regionId}/academies?page=1&limit=100`);
      if (response.data.success) {
        setAcademies(response.data.data.academies || []);
      }
    } catch (error) {
      console.error('Error fetching academies:', error);
      toast.error(t('users.academiesFetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch districts by academy
  const fetchDistrictsByAcademy = async (academyId) => {
    if (!academyId) {
      setDistricts([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/academies/${academyId}/districts?page=1&limit=100`);
      if (response.data.success) {
        setDistricts(response.data.data.districts || []);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error(t('users.districtsFetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch institutions by district
  const fetchInstitutionsByDistrict = async (districtId) => {
    if (!districtId) {
      setInstitutions([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/districts/${districtId}/institutions?page=1&limit=100`);
      if (response.data.success) {
        setInstitutions(response.data.data.institutions || []);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
      toast.error(t('users.institutionsFetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch departments by institution
  const fetchDepartmentsByInstitution = async (institutionId) => {
    if (!institutionId) {
      setDepartments([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/institutions/${institutionId}/departments?page=1&limit=100`);
      if (response.data.success) {
        setDepartments(response.data.data.departments || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error(t('users.departmentsFetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Check if user has student account
  const checkStudentAccountStatus = async (userId) => {
    if (checkingStudentStatus[userId]) return; // تجنب الطلبات المكررة
    
    setCheckingStudentStatus(prev => ({ ...prev, [userId]: true }));
    
    try {
      const response = await api.get(`/students/user/${userId}`);
      
      if (response.data.success && response.data.data && response.data.data.student) {
        setStudentAccountStatus(prev => ({ ...prev, [userId]: true }));
        console.log(`User ${userId} has student account:`, response.data.data.student);
      } else {
        setStudentAccountStatus(prev => ({ ...prev, [userId]: false }));
        console.log(`User ${userId} does not have student account`);
      }
    } catch (error) {
      // إذا كان الخطأ 404، فهذا يعني أن المستخدم ليس لديه حساب طالب
      if (error.response?.status === 404) {
        setStudentAccountStatus(prev => ({ ...prev, [userId]: false }));
        console.log(`User ${userId} does not have student account (404)`);
      } else {
        console.error(`Error checking student account for user ${userId}:`, error);
        setStudentAccountStatus(prev => ({ ...prev, [userId]: false }));
      }
    } finally {
      setCheckingStudentStatus(prev => ({ ...prev, [userId]: false }));
    }
  };

  const openChangePasswordModal = (user) => {
    setPasswordUser(user);
    setShowPasswordModal(true);
    setNewPassword('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUser(null);
    setNewPassword('');
    setUpdatingPassword(false);
  };

  const generateRandomPassword = () => {
    const base = passwordUser?.username || 'User';
    const suffix = Math.random().toString(36).slice(-4);
    const candidate = `${base}@${suffix}123`;
    setNewPassword(candidate);
  };

  const copyNewPasswordToClipboard = async () => {
    if (!newPassword) return;
    try {
      await navigator.clipboard.writeText(newPassword);
      toast.success(t('settings.password.copied'), { duration: 2000, position: 'top-center' });
    } catch (error) {
      console.error('Clipboard copy error:', error);
      toast.error(t('settings.password.copyError'), { duration: 2000, position: 'top-center' });
    }
  };

  const updateUserPassword = async () => {
    if (!passwordUser || !newPassword.trim()) {
      toast.error(t('users.newPasswordRequired') || 'New password is required', {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }
    setUpdatingPassword(true);
    try {
      const payload = { newPassword: newPassword.trim() };
      const response = await api.put(`/users/${passwordUser.id}/password`, payload);
      if (response.data?.success) {
        toast.success(
          response.data.message || t('users.passwordUpdateSuccess') || 'Password updated successfully',
          { duration: 3000, position: 'top-center' }
        );
        closePasswordModal();
      } else {
        toast.error(
          response.data?.message || t('users.passwordUpdateError') || 'Failed to update password',
          { duration: 3000, position: 'top-center' }
        );
      }
    } catch (error) {
      console.error('Error updating user password:', error);
      toast.error(
        error.response?.data?.message || t('users.passwordUpdateError') || 'Failed to update password',
        { duration: 3000, position: 'top-center' }
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  const savePasswordAndSendWhatsApp = async () => {
    if (!passwordUser || !newPassword.trim()) {
      toast.error(t('users.newPasswordRequired') || 'New password is required', {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }
    const waPhone = normalizePhoneForWhatsApp(passwordUser.phone);
    if (!waPhone || waPhone.length < 8) {
      toast.error(t('users.whatsAppPhoneRequired'), { duration: 4000, position: 'top-center' });
      return;
    }
    setUpdatingPassword(true);
    try {
      const payload = { newPassword: newPassword.trim() };
      const response = await api.put(`/users/${passwordUser.id}/password`, payload);
      if (!response.data?.success) {
        toast.error(
          response.data?.message || t('users.passwordUpdateError') || 'Failed to update password',
          { duration: 3000, position: 'top-center' }
        );
        return;
      }
      toast.success(
        response.data.message || t('users.passwordUpdateSuccess') || 'Password updated successfully',
        { duration: 3000, position: 'top-center' }
      );
      const loginUrl = `${window.location.origin}/login`;
      const message = t('users.whatsAppCredentialsMessage', {
        email: passwordUser.email || '—',
        username: passwordUser.username || '—',
        password: newPassword.trim(),
        loginUrl
      });
      const waUrl = `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`;
      const opened = window.open(waUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        toast.error(t('users.whatsAppPopupBlocked'), { duration: 5000, position: 'top-center' });
      }
      closePasswordModal();
    } catch (error) {
      console.error('Error updating user password (WhatsApp flow):', error);
      toast.error(
        error.response?.data?.message || t('users.passwordUpdateError') || 'Failed to update password',
        { duration: 3000, position: 'top-center' }
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  // Load users, roles, and departments on component mount
  useEffect(() => {
    // إلغاء جميع الفلترات في الوضع الافتراضي
    setFilters({
      roleId: '',
      departmentId: ''
    });
    setAppliedFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setShowAdvancedFilters(false);
    
    // تحميل المستخدمين بدون فلترات
    fetchUsers(1, '', {});
    fetchRoles();
    // تحميل الأقسام لاستخدامها في فلتر القسم
    (async () => {
      try {
        let allDepartments = [];
        let currentPage = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await api.get(`/organization/departments?page=${currentPage}&limit=100`);
          if (res.data?.success) {
            const fetched = res.data.data?.departments || [];
            allDepartments = [...allDepartments, ...fetched];
            const pagination = res.data.data?.pagination;
            if (pagination && currentPage < pagination.pages) {
              currentPage += 1;
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }
        setDepartments(allDepartments);
      } catch (error) {
        console.error('Error fetching departments for users filter:', error);
        toast.error(t('users.departmentsFetchError'), { duration: 3000, position: 'top-center' });
      }
    })();
  }, []);

  // تحديث الترجمات عند تغيير اللغة
  useEffect(() => {
    if (learningLevels.length > 0) {
      const updatedLevels = learningLevels.map(level => {
        const levelKey = level.originalName || level.name;
        const translations = learningLevelsData[levelKey] || {};
        
        return {
          ...level,
          translatedName: translations[currentLanguage] || level.name
        };
      });
      
      setLearningLevels(updatedLevels);
    }
  }, [currentLanguage]);

  // تحقق تلقائي من وجود حساب الطالب للطلاب
  useEffect(() => {
    users.forEach(user => {
      if (user.roles?.[0] === 'student' && studentAccountStatus[user.id] === undefined && !checkingStudentStatus[user.id]) {
        checkStudentAccountStatus(user.id);
      }
    });
  }, [users]);

  // Debounced search
  useEffect(() => {
    if (searchTerm === '') {
      fetchUsers(1, '', appliedFilters);
    } else {
      const timeoutId = setTimeout(() => {
        setIsSearching(true);
        fetchUsers(1, searchTerm, appliedFilters);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  // Refetch when limit changes (skip initial mount)
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    fetchUsers(1, searchTerm, appliedFilters);
  }, [limit]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    fetchUsers(1, searchTerm, appliedFilters);
  };

  // Apply filters
  const applyFilters = () => {
    const newFilters = { ...filters };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchUsers(1, searchTerm, newFilters);
    setShowAdvancedFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      roleId: ''
    });
    setAppliedFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setShowAdvancedFilters(false);
    fetchUsers(1, '', {});
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (appliedFilters.roleId) count++;
    if (appliedFilters.departmentId) count++;
    return count;
  };


  // Open add modal
  const openAddModal = () => {
    setNewUser({
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      phone: '',
      dateOfBirth: '',
      gender: 'other'
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewUser({
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      phone: '',
      dateOfBirth: '',
      gender: 'other'
    });
    setFormErrors({});
  };

  // Excel template columns (API keys)
  const EXCEL_COLUMNS = ['firstName', 'lastName', 'username', 'email', 'password', 'phone', 'dateOfBirth', 'gender'];

  // Download Excel template
  const downloadTemplate = () => {
    const wsData = [
      EXCEL_COLUMNS,
      ['John', 'Doe', 'johndoe', 'john@example.com', 'Password123', '+1234567890', '1990-01-15', 'male']
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'users_template.xlsx');
    toast.success(t('users.templateDownloaded'), { duration: 3000, position: 'top-center' });
  };

  // Parse and validate Excel rows
  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          if (rows.length < 2) {
            reject(new Error(t('users.importNoData')));
            return;
          }
          const headers = rows[0];
          const dataRows = rows.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== ''));
          const users = dataRows.map((row) => {
            const user = {};
            EXCEL_COLUMNS.forEach((col, i) => {
              const val = row[i] !== undefined && row[i] !== null ? String(row[i]).trim() : '';
              user[col] = col === 'dateOfBirth' ? (val || null) : (col === 'gender' ? (val || 'other') : val);
            });
            return user;
          });
          resolve(users);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error(t('users.importReadError')));
      reader.readAsArrayBuffer(file);
    });
  };

  // Import users from Excel
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      toast.error(t('users.importInvalidFormat'), { duration: 3000, position: 'top-center' });
      return;
    }
    event.target.value = '';
    setShowImportModal(true);
    setIsImporting(true);
    setImportResults({ success: 0, failed: 0, errors: [] });
    try {
      const users = await parseExcelFile(file);
      if (users.length === 0) {
        toast.error(t('users.importNoData'), { duration: 3000, position: 'top-center' });
        setIsImporting(false);
        setShowImportModal(false);
        return;
      }
      setImportProgress({ current: 0, total: users.length });
      let success = 0;
      const errors = [];
      for (let i = 0; i < users.length; i++) {
        const u = users[i];
        if (!u.firstName || !u.lastName || !u.username || !u.email || !u.password || !u.phone) {
          errors.push({ row: i + 2, msg: t('users.importRowIncomplete') });
          setImportProgress({ current: i + 1, total: users.length });
          continue;
        }
        if (!/\S+@\S+\.\S+/.test(u.email)) {
          errors.push({ row: i + 2, msg: t('users.emailInvalid') });
          setImportProgress({ current: i + 1, total: users.length });
          continue;
        }
        try {
          const payload = {
            firstName: u.firstName,
            lastName: u.lastName,
            username: u.username,
            email: u.email,
            password: u.password,
            phone: u.phone,
            dateOfBirth: u.dateOfBirth || null,
            gender: u.gender || 'other'
          };
          const res = await api.post('/auth/register', payload);
          if (res.data.success) {
            success++;
          } else {
            errors.push({ row: i + 2, msg: res.data.message || t('users.addError') });
          }
        } catch (err) {
          errors.push({ row: i + 2, msg: err.response?.data?.message || err.message || t('users.addError') });
        }
        setImportProgress({ current: i + 1, total: users.length });
      }
      const failed = users.length - success;
      setImportResults({ success, failed, errors });
      if (success > 0) {
        fetchUsers(1, '', {});
        toast.success(t('users.importSuccess', { count: success }), { duration: 3000, position: 'top-center' });
      }
      if (failed > 0 && success === 0) {
        toast.error(t('users.importAllFailed'), { duration: 3000, position: 'top-center' });
      }
    } catch (err) {
      toast.error(err.message || t('users.importError'), { duration: 3000, position: 'top-center' });
      setShowImportModal(false);
    } finally {
      setIsImporting(false);
    }
  };

  // Add user
  const addUser = async () => {
    const errors = {};
    if (!newUser.firstName?.trim()) {
      errors.firstName = t('users.firstNameRequired');
    }
    if (!newUser.lastName?.trim()) {
      errors.lastName = t('users.lastNameRequired');
    }
    if (!newUser.username?.trim()) {
      errors.username = t('users.usernameRequired');
    }
    if (!newUser.email?.trim()) {
      errors.email = t('users.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(newUser.email)) {
      errors.email = t('users.emailInvalid');
    }
    if (!newUser.password?.trim()) {
      errors.password = t('users.passwordRequired');
    }
    if (!newUser.phone?.trim()) {
      errors.phone = t('users.phoneRequired');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        username: newUser.username?.trim(),
        email: newUser.email?.trim(),
        password: newUser.password?.trim(),
        firstName: newUser.firstName?.trim(),
        lastName: newUser.lastName?.trim(),
        phone: newUser.phone?.trim(),
        dateOfBirth: newUser.dateOfBirth || null,
        gender: newUser.gender || 'other'
      };
      
      // التأكد من أن الحقول المطلوبة ليست فارغة
      if (!requestData.firstName || !requestData.lastName || !requestData.username || !requestData.email || !requestData.password) {
        toast.error(t('users.allRequiredFieldsMustBeFilled'), { duration: 3000, position: 'top-center' });
        setIsAdding(false);
        return;
      }
      
      // إزالة الحقول الفارغة فقط إذا كانت null أو undefined أو string فارغ للحقول الاختيارية
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined || requestData[key] === null) {
          delete requestData[key];
        } else if (key === 'dateOfBirth' && (!requestData[key] || requestData[key] === '')) {
          delete requestData[key];
        } else if (key === 'phone' && requestData[key] === '') {
          // لا نحذف الهاتف لأنه مطلوب
          // لكن يمكن إزالة هذا الشرط إذا كان الهاتف اختيارياً
        }
      });
      
      console.log('Add User Request Data:', requestData);
      console.log('API Endpoint: /auth/register');
      console.log('Full URL will be:', `${api.defaults.baseURL}/auth/register`);
      console.log('Request headers:', api.defaults.headers);
      
      const response = await api.post('/auth/register', requestData);
      
      console.log('Add User Response:', response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.addSuccess'), { duration: 3000, position: 'top-center' });
        closeAddModal();
        // إعادة تحميل قائمة المستخدمين
        fetchUsers(1, '', {});
      } else {
        toast.error(response.data.message || t('users.addError'), { duration: 3000, position: 'top-center' });
      }
    } catch (error) {
      console.error('Error adding user:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      
      let errorMessage = t('users.addError');
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        // معالجة أخطاء التحقق من صحة البيانات
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors)) {
          errorMessage = validationErrors.join(', ');
        } else if (typeof validationErrors === 'object') {
          errorMessage = Object.values(validationErrors).join(', ');
        }
      } else if (error.response?.status === 400) {
        errorMessage = 'بيانات غير صحيحة. يرجى التحقق من جميع الحقول.';
      } else if (error.response?.status === 409) {
        errorMessage = 'اسم المستخدم أو البريد الإلكتروني موجود مسبقاً.';
      } else if (error.response?.status === 422) {
        errorMessage = 'خطأ في التحقق من صحة البيانات.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'خطأ في الخادم. يرجى المحاولة لاحقاً.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 5000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    // تحويل التاريخ إلى الصيغة المطلوبة لحقل input[type="date"]
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // "1988-06-10"
    };

    const editingUserData = {
      id: user.id,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phone: user.phone || '',
      dateOfBirth: formatDateForInput(user.date_of_birth),
      gender: user.gender || 'other',
      username: user.username || '',
      email: user.email || ''
    };
    
    console.log('User data for editing:', user);
    console.log('Original date_of_birth:', user.date_of_birth);
    console.log('Formatted dateOfBirth:', editingUserData.dateOfBirth);
    console.log('Editing user data:', editingUserData);
    
    setEditingUser(editingUserData);
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Update user
  const updateUser = async () => {
    const errors = {};
    if (!editingUser.firstName?.trim()) {
      errors.firstName = t('users.firstNameRequired');
    }
    if (!editingUser.lastName?.trim()) {
      errors.lastName = t('users.lastNameRequired');
    }
    if (!editingUser.phone?.trim()) {
      errors.phone = t('users.phoneRequired');
    }
    
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsEditing(true);
    try {
      const requestData = {
        firstName: editingUser.firstName?.trim() || '',
        lastName: editingUser.lastName?.trim() || '',
        phone: editingUser.phone?.trim() || '',
        dateOfBirth: editingUser.dateOfBirth || null,
        gender: editingUser.gender || 'other'
      };
      
      // التأكد من أن الحقول المطلوبة ليست فارغة
      if (!requestData.firstName || !requestData.lastName) {
        toast.error(t('users.firstNameLastNameRequired'), { duration: 3000, position: 'top-center' });
        return;
      }
      
      // إزالة أي قيم undefined أو null من requestData
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined || requestData[key] === null) {
          delete requestData[key];
        }
      });
      
      console.log('Update User Request Data:', requestData);
      console.log('User ID:', editingUser.id);
      console.log('Full URL:', `/users/${editingUser.id}`);
      
      const response = await api.put(`/users/${editingUser.id}`, requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.updateSuccess'), { duration: 3000, position: 'top-center' });
        setShowEditModal(false);
        setEditingUser({
          id: '',
          firstName: '',
          lastName: '',
          phone: '',
          dateOfBirth: '',
          gender: 'other'
        });
        setEditFormErrors({});
        fetchUsers(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      
      const errorMessage = error.response?.data?.message || error.message || t('users.updateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsEditing(false);
    }
  };

  // Delete user with warning confirmation
  const deleteUserWithWarning = async (userId, userName) => {
    if (window.confirm(t('users.confirmDeleteWithWarning', { name: userName }))) {
      try {
        const response = await api.put(`/users/${userId}/deactivate`);
        
        if (response.data.success) {
          toast.success(response.data.message || t('users.deleteSuccess'), { duration: 3000, position: 'top-center' });
          fetchUsers(currentPage, searchTerm, appliedFilters);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error.response?.data?.message || error.message || t('users.deleteError');
        toast.error(errorMessage, { duration: 3000, position: 'top-center' });
      }
    }
  };

  // Open role assignment modal
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setSelectedRoleId('');
    setShowRoleModal(true);
  };

  // Assign role to user
  const assignRoleToUser = async () => {
    if (!selectedRoleId) {
      toast.error(t('users.selectRoleFirst'), { duration: 3000, position: 'top-center' });
      return;
    }

    setIsAssigningRole(true);
    try {
      const response = await api.post(`/users/${selectedUser.id}/assign-role`, {
        roleId: parseInt(selectedRoleId),
        action: 'assign',
        expiresAt: null
      });
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.roleAssignedSuccess'), { duration: 3000, position: 'top-center' });
        setSelectedRoleId('');
        fetchUsers(currentPage, searchTerm, appliedFilters);
        setShowRoleModal(false);
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.roleAssignError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAssigningRole(false);
    }
  };

  // Remove role from user
  const removeRoleFromUser = async (userId, roleId) => {
    if (!roleId) return;
    setRemovingRoleId(roleId);
    try {
      const response = await api.post(`/users/${userId}/assign-role`, {
        roleId: parseInt(roleId),
        action: 'remove'
      });
      if (response.data.success) {
        toast.success(response.data.message || t('users.roleRemovedSuccess'), { duration: 3000, position: 'top-center' });
        fetchUsers(currentPage, searchTerm, appliedFilters);
        setSelectedUser(prev => {
          if (!prev) return prev;
          const newRoles = (prev.roles || []).filter(
            (r) => roles.find(role => role.id === roleId)?.name !== r
          );
          return { ...prev, roles: newRoles };
        });
      }
    } catch (error) {
      console.error('Error removing role:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.roleRemoveError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setRemovingRoleId(null);
    }
  };

  // Toggle user status
  const toggleUserVerification = async (userId, currentVerificationStatus) => {
    try {
      const response = await api.put(`/users/${userId}/verify`, {
        isVerified: !currentVerificationStatus
      });
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.verificationUpdated'), { duration: 3000, position: 'top-center' });
        fetchUsers(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error toggling user verification:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.verificationUpdateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    }
  };

  // Toggle user activation/deactivation
  const toggleUserActivation = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus === 1 ? `/users/${userId}/deactivate` : `/users/${userId}/activate`;
      const response = await api.put(endpoint);
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.activationUpdated'), { duration: 3000, position: 'top-center' });
        fetchUsers(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error toggling user activation:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.activationUpdateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    }
  };

  // Open student account creation modal
  const openStudentModal = (user) => {
    setSelectedUserForStudent(user);
    setStudentFormData({
      userId: user.id,
      studentNumber: '',
      currentLevelId: '',
      enrollmentDate: new Date().toISOString().split('T')[0] // اليوم الحالي كتاريخ افتراضي
    });
    setStudentFormErrors({});
    setShowStudentModal(true);
    fetchLearningLevels(); // جلب المستويات التعليمية عند فتح المودال
  };

  // Close student modal
  const closeStudentModal = () => {
    setShowStudentModal(false);
    setSelectedUserForStudent(null);
    setStudentFormData({
      userId: '',
      studentNumber: '',
      currentLevelId: '',
      enrollmentDate: ''
    });
    setStudentFormErrors({});
  };

  // Users on current page who can get a student profile (role student, no profile yet)
  const selectableStudentUserIds = users.filter(
    (u) => u.roles?.[0] === 'student' && studentAccountStatus[u.id] === false
  ).map((u) => u.id);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageUserIds = users.map((u) => u.id);
    const allSelected = pageUserIds.every((id) => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !pageUserIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => [...new Set([...prev, ...pageUserIds])]);
    }
  };

  const openBulkStudentModal = () => {
    const eligible = selectedUserIds.filter((id) => selectableStudentUserIds.includes(id));
    if (eligible.length === 0) {
      toast.error(t('users.bulkStudentNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }
    setBulkStudentFormData({
      currentLevelId: '',
      enrollmentDate: '',
      studentNumberPrefix: 'STU'
    });
    setBulkStudentFormErrors({});
    setBulkStudentResults({ success: 0, failed: 0, errors: [] });
    setShowBulkStudentModal(true);
    fetchLearningLevels();
  };

  const closeBulkStudentModal = () => {
    setShowBulkStudentModal(false);
    setBulkStudentProgress({ current: 0, total: 0 });
    setSelectedUserIds([]);
  };

  const openBulkOrgModal = () => {
    if (selectedUserIds.length === 0) {
      toast.error(t('users.bulkNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }
    setBulkOrgFormData({
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    });
    setBulkOrgFormErrors({});
    setBulkOrgResults({ success: 0, failed: 0, errors: [] });
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    setShowBulkOrgModal(true);
    setLoadingBulkOrgScope(true);
    setOrgScopeFromCurrentUser(null);
    setOrgScopeNamesFromCurrentUser(null);

    authService.getCurrentUser()
      .then((me) => {
        const access = me?.organizationalAccess?.[0];
        const scope = normalizeOrgScope(access);
        const scopeNames = normalizeOrgScopeNames(access);
        setOrgScopeFromCurrentUser(scope || null);
        setOrgScopeNamesFromCurrentUser(scopeNames || null);
        setBulkOrgFormData((prev) => ({
          ...prev,
          countryId: scope?.countryId ?? prev.countryId,
          regionId: scope?.regionId ?? prev.regionId,
          academyId: scope?.academyId ?? prev.academyId,
          districtId: scope?.districtId ?? prev.districtId,
          institutionId: scope?.institutionId ?? prev.institutionId,
          departmentId: scope?.departmentId ?? prev.departmentId
        }));
        const hasScopeIds = scope?.countryId != null && scope?.countryId !== '';
        if (hasScopeIds) {
          fetchOrganizationalData().catch(() => {});
          if (scope?.countryId) fetchRegionsByCountry(scope.countryId).catch(() => {});
          if (scope?.regionId) fetchAcademiesByRegion(scope.regionId).catch(() => {});
          if (scope?.academyId) fetchDistrictsByAcademy(scope.academyId).catch(() => {});
          if (scope?.districtId) fetchInstitutionsByDistrict(scope.districtId).catch(() => {});
          if (scope?.institutionId) fetchDepartmentsByInstitution(scope.institutionId).catch(() => {});
          return Promise.resolve();
        }
        if (scopeNames && (scopeNames.countryName || scopeNames.regionName)) {
          return resolveOrgNamesToIdsAndLoadLists(scopeNames, setBulkOrgFormData);
        }
        fetchOrganizationalData().catch(() => {});
        return Promise.resolve();
      })
      .catch(() => {
        setOrgScopeFromCurrentUser(null);
        setOrgScopeNamesFromCurrentUser(null);
      })
      .finally(() => setLoadingBulkOrgScope(false));
  };

  const closeBulkOrgModal = () => {
    setShowBulkOrgModal(false);
    setBulkOrgProgress({ current: 0, total: 0 });
    setSelectedUserIds([]);
    setOrgScopeFromCurrentUser(null);
    setOrgScopeNamesFromCurrentUser(null);
  };

  const handleBulkOrgCountryChange = (countryId) => {
    setBulkOrgFormData((prev) => ({
      ...prev,
      countryId: countryId || '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (countryId) fetchRegionsByCountry(countryId);
  };

  const handleBulkOrgRegionChange = (regionId) => {
    setBulkOrgFormData((prev) => ({
      ...prev,
      regionId: regionId || '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (regionId) fetchAcademiesByRegion(regionId);
  };

  const handleBulkOrgAcademyChange = (academyId) => {
    setBulkOrgFormData((prev) => ({
      ...prev,
      academyId: academyId || '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (academyId) fetchDistrictsByAcademy(academyId);
  };

  const handleBulkOrgDistrictChange = (districtId) => {
    setBulkOrgFormData((prev) => ({
      ...prev,
      districtId: districtId || '',
      institutionId: '',
      departmentId: ''
    }));
    setInstitutions([]);
    setDepartments([]);
    if (districtId) fetchInstitutionsByDistrict(districtId);
  };

  const handleBulkOrgInstitutionChange = (institutionId) => {
    setBulkOrgFormData((prev) => ({
      ...prev,
      institutionId: institutionId || '',
      departmentId: ''
    }));
    setDepartments([]);
    if (institutionId) fetchDepartmentsByInstitution(institutionId);
  };

  const handleBulkOrgDepartmentChange = (departmentId) => {
    setBulkOrgFormData((prev) => ({ ...prev, departmentId: departmentId || '' }));
  };

  const assignBulkOrganization = async () => {
    const countryId = scopeOrForm('countryId', bulkOrgFormData);
    const errors = {};
    if (!countryId) errors.countryId = t('users.countryRequired');
    setBulkOrgFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const regionId = scopeOrForm('regionId', bulkOrgFormData);
    const academyId = scopeOrForm('academyId', bulkOrgFormData);
    const districtId = scopeOrForm('districtId', bulkOrgFormData);
    const institutionId = scopeOrForm('institutionId', bulkOrgFormData);
    const departmentId = scopeOrForm('departmentId', bulkOrgFormData);

    setIsAssigningBulkOrg(true);
    setBulkOrgProgress({ current: 0, total: selectedUserIds.length });
    setBulkOrgResults({ success: 0, failed: 0, errors: [] });

    let success = 0;
    const resultErrors = [];
    for (let i = 0; i < selectedUserIds.length; i++) {
      const userId = selectedUserIds[i];
      setBulkOrgProgress({ current: i + 1, total: selectedUserIds.length });
      try {
        const requestData = { userId: parseInt(userId) };
        if (countryId) requestData.countryId = parseInt(countryId);
        if (regionId) requestData.regionId = parseInt(regionId);
        if (academyId) requestData.academyId = parseInt(academyId);
        if (districtId) requestData.districtId = parseInt(districtId);
        if (institutionId) requestData.institutionId = parseInt(institutionId);
        if (departmentId) requestData.departmentId = parseInt(departmentId);
        requestData.expiresAt = null;

        const res = await api.post('/organization/assign-user', requestData);
        if (res.data.success) success++;
        else resultErrors.push({ userId, name: users.find((u) => u.id === userId)?.first_name + ' ' + (users.find((u) => u.id === userId)?.last_name || ''), msg: res.data.message || t('users.orgAssignmentError') });
      } catch (err) {
        resultErrors.push({
          userId,
          name: (users.find((u) => u.id === userId)?.first_name || '') + ' ' + (users.find((u) => u.id === userId)?.last_name || ''),
          msg: err.response?.data?.message || err.message || t('users.orgAssignmentError')
        });
      }
    }
    setBulkOrgResults({ success, failed: selectedUserIds.length - success, errors: resultErrors });
    setIsAssigningBulkOrg(false);
    if (success > 0) {
      fetchUsers(currentPage, searchTerm, appliedFilters);
      toast.success(t('users.bulkOrgSuccess', { count: success }), { duration: 3000, position: 'top-center' });
    }
    if (selectedUserIds.length - success > 0 && success === 0) {
      toast.error(t('users.bulkOrgAllFailed'), { duration: 3000, position: 'top-center' });
    }
  };

  const openBulkStatusModal = () => {
    if (selectedUserIds.length === 0) {
      toast.error(t('users.bulkNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }
    setBulkStatusValue(true);
    setBulkStatusResults({ success: 0, failed: 0, errors: [] });
    setShowBulkStatusModal(true);
  };

  const closeBulkStatusModal = () => {
    setShowBulkStatusModal(false);
    setBulkStatusProgress({ current: 0, total: 0 });
    setSelectedUserIds([]);
  };

  const updateBulkStatus = async () => {
    setIsUpdatingBulkStatus(true);
    setBulkStatusProgress({ current: 0, total: selectedUserIds.length });
    setBulkStatusResults({ success: 0, failed: 0, errors: [] });

    let success = 0;
    const resultErrors = [];
    for (let i = 0; i < selectedUserIds.length; i++) {
      const userId = selectedUserIds[i];
      setBulkStatusProgress({ current: i + 1, total: selectedUserIds.length });
      try {
        const res = await api.put(`/users/${userId}/verify`, { isVerified: bulkStatusValue });
        if (res.data.success) success++;
        else resultErrors.push({ userId, name: users.find((u) => u.id === userId)?.first_name + ' ' + (users.find((u) => u.id === userId)?.last_name || ''), msg: res.data.message || t('users.verificationUpdateError') });
      } catch (err) {
        resultErrors.push({
          userId,
          name: (users.find((u) => u.id === userId)?.first_name || '') + ' ' + (users.find((u) => u.id === userId)?.last_name || ''),
          msg: err.response?.data?.message || err.message || t('users.verificationUpdateError')
        });
      }
    }
    setBulkStatusResults({ success, failed: selectedUserIds.length - success, errors: resultErrors });
    setIsUpdatingBulkStatus(false);
    if (success > 0) {
      fetchUsers(currentPage, searchTerm, appliedFilters);
      toast.success(t('users.bulkStatusSuccess', { count: success }), { duration: 3000, position: 'top-center' });
    }
    if (selectedUserIds.length - success > 0 && success === 0) {
      toast.error(t('users.bulkStatusAllFailed'), { duration: 3000, position: 'top-center' });
    }
  };

  const createBulkStudentProfiles = async () => {
    const eligible = selectedUserIds.filter((id) => selectableStudentUserIds.includes(id));
    if (eligible.length === 0) {
      toast.error(t('users.bulkStudentNoSelection'), { duration: 3000, position: 'top-center' });
      return;
    }
    const errors = {};
    if (!bulkStudentFormData.currentLevelId) {
      errors.currentLevelId = t('users.currentLevelRequired');
    }
    if (!bulkStudentFormData.enrollmentDate) {
      errors.enrollmentDate = t('users.enrollmentDateRequired');
    }
    setBulkStudentFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsCreatingBulkStudents(true);
    setBulkStudentProgress({ current: 0, total: eligible.length });
    setBulkStudentResults({ success: 0, failed: 0, errors: [] });

    let success = 0;
    const resultErrors = [];
    const prefix = (bulkStudentFormData.studentNumberPrefix || 'STU').trim() || 'STU';

    for (let i = 0; i < eligible.length; i++) {
      const userId = eligible[i];
      const studentNumber = prefix + String(i + 1).padStart(4, '0');
      setBulkStudentProgress({ current: i + 1, total: eligible.length });

      try {
        const res = await api.post('/students', {
          userId: parseInt(userId),
          studentNumber,
          currentLevelId: parseInt(bulkStudentFormData.currentLevelId),
          enrollmentDate: bulkStudentFormData.enrollmentDate
        });
        if (res.data.success) {
          success++;
          setStudentAccountStatus((prev) => ({ ...prev, [userId]: true }));
        } else {
          resultErrors.push({ userId, name: users.find((u) => u.id === userId)?.first_name + ' ' + (users.find((u) => u.id === userId)?.last_name || ''), msg: res.data.message || t('users.studentAccountCreationError') });
        }
      } catch (err) {
        resultErrors.push({
          userId,
          name: (users.find((u) => u.id === userId)?.first_name || '') + ' ' + (users.find((u) => u.id === userId)?.last_name || ''),
          msg: err.response?.data?.message || err.message || t('users.studentAccountCreationError')
        });
      }
    }

    setBulkStudentResults({ success, failed: eligible.length - success, errors: resultErrors });
    setIsCreatingBulkStudents(false);
    if (success > 0) {
      fetchUsers(currentPage, searchTerm, appliedFilters);
      toast.success(t('users.bulkStudentSuccess', { count: success }), { duration: 3000, position: 'top-center' });
    }
    if (eligible.length - success > 0 && success === 0) {
      toast.error(t('users.bulkStudentAllFailed'), { duration: 3000, position: 'top-center' });
    }
  };

  // Create student account
  const createStudentAccount = async () => {
    const errors = {};
    
    if (!studentFormData.studentNumber?.trim()) {
      errors.studentNumber = t('users.studentNumberRequired');
    }
    if (!studentFormData.currentLevelId) {
      errors.currentLevelId = t('users.currentLevelRequired');
    }
    if (!studentFormData.enrollmentDate) {
      errors.enrollmentDate = t('users.enrollmentDateRequired');
    }
    
    setStudentFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsCreatingStudent(true);
    try {
      const requestData = {
        userId: parseInt(studentFormData.userId),
        studentNumber: studentFormData.studentNumber.trim(),
        currentLevelId: parseInt(studentFormData.currentLevelId),
        enrollmentDate: studentFormData.enrollmentDate
      };
      
      console.log('Create Student Account Request Data:', requestData);
      console.log('API Endpoint: /students');
      
      const response = await api.post('/students', requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.studentAccountCreated'), { duration: 3000, position: 'top-center' });
        
        // تحديث حالة حساب الطالب ليصبح موجود
        setStudentAccountStatus(prev => ({ ...prev, [studentFormData.userId]: true }));
        
        closeStudentModal();
        fetchUsers(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error creating student account:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = t('users.studentAccountCreationError');
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsCreatingStudent(false);
    }
  };

  // دمج هيكل المستخدم الحالي (المقفل) مع هيكل المستخدم المستهدف — المستويات المقفلة من scope والباقي من target
  const mergeScopeWithTargetOrg = (scope, targetScope) => {
    const fixed = (key) => scope && scope[key] !== '' && scope[key] != null;
    return {
      countryId: fixed('countryId') ? scope.countryId : (targetScope?.countryId ?? ''),
      regionId: fixed('regionId') ? scope.regionId : (targetScope?.regionId ?? ''),
      academyId: fixed('academyId') ? scope.academyId : (targetScope?.academyId ?? ''),
      districtId: fixed('districtId') ? scope.districtId : (targetScope?.districtId ?? ''),
      institutionId: fixed('institutionId') ? scope.institutionId : (targetScope?.institutionId ?? ''),
      departmentId: fixed('departmentId') ? scope.departmentId : (targetScope?.departmentId ?? '')
    };
  };

  // Open organizational assignment modal — جلب هيكل المستخدم الحالي من API ثم هيكل المستخدم المستهدف ودمجهما
  const openOrgModal = (user) => {
    setSelectedUserForOrg(user);
    setOrgFormData({
      userId: user.id,
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    });
    setOrgFormErrors({});
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    setShowOrgModal(true);
    setLoadingTargetUserOrg(true);
    setOrgScopeFromCurrentUser(null);
    setOrgScopeNamesFromCurrentUser(null);

    // جلب هيكل المستخدم الحالي من /auth/me لضمان وجود organizationalAccess وتحديد المستويات المقفلة
    authService.getCurrentUser()
      .then((me) => {
        const access = me?.organizationalAccess?.[0];
        const scope = normalizeOrgScope(access);
        const scopeNames = normalizeOrgScopeNames(access);
        setOrgScopeFromCurrentUser(scope || null);
        setOrgScopeNamesFromCurrentUser(scopeNames || null);
        setOrgFormData((prev) => ({
          ...prev,
          countryId: scope?.countryId ?? prev.countryId,
          regionId: scope?.regionId ?? prev.regionId,
          academyId: scope?.academyId ?? prev.academyId,
          districtId: scope?.districtId ?? prev.districtId,
          institutionId: scope?.institutionId ?? prev.institutionId,
          departmentId: scope?.departmentId ?? prev.departmentId
        }));
        fetchOrganizationalData().catch(() => {});
        if (scope?.countryId) fetchRegionsByCountry(scope.countryId).catch(() => {});
        if (scope?.regionId) fetchAcademiesByRegion(scope.regionId).catch(() => {});
        if (scope?.academyId) fetchDistrictsByAcademy(scope.academyId).catch(() => {});
        if (scope?.districtId) fetchInstitutionsByDistrict(scope.districtId).catch(() => {});
        if (scope?.institutionId) fetchDepartmentsByInstitution(scope.institutionId).catch(() => {});
        return { scope: scope || null, scopeNames: scopeNames || null };
      })
      .catch(() => {
        setOrgScopeFromCurrentUser(null);
        setOrgScopeNamesFromCurrentUser(null);
        return { scope: null, scopeNames: null };
      })
      .then(({ scope, scopeNames }) => {
        return api.get(`/users/${user.id}`)
          .then((res) => {
            const data = res.data?.data;
            const targetAccess = data?.organizationalAccess?.[0] ?? data?.user?.organizationalAccess?.[0];
            const targetScope = normalizeOrgScope(targetAccess);
            const merged = mergeScopeWithTargetOrg(scope || {}, targetScope);
            setOrgFormData((prev) => ({ ...prev, ...merged }));
            const c = merged.countryId || scope?.countryId;
            const r = merged.regionId || scope?.regionId;
            const a = merged.academyId || scope?.academyId;
            const d = merged.districtId || scope?.districtId;
            const i = merged.institutionId || scope?.institutionId;
            if (c) fetchRegionsByCountry(c).catch(() => {});
            if (r) fetchAcademiesByRegion(r).catch(() => {});
            if (a) fetchDistrictsByAcademy(a).catch(() => {});
            if (d) fetchInstitutionsByDistrict(d).catch(() => {});
            if (i) fetchDepartmentsByInstitution(i).catch(() => {});
            const hasScopeIds = scope?.countryId != null && scope?.countryId !== '';
            if (!hasScopeIds && scopeNames?.countryName) {
              return resolveOrgNamesToIdsAndLoadLists(scopeNames, null).then((resolved) => {
                if (resolved && resolved.countryId) {
                  setOrgScopeFromCurrentUser(resolved);
                  setOrgFormData((prev) => ({ ...prev, ...mergeScopeWithTargetOrg(resolved, targetScope) }));
                  const rc = resolved.countryId;
                  const rr = resolved.regionId;
                  const ra = resolved.academyId;
                  const rd = resolved.districtId;
                  const ri = resolved.institutionId;
                  if (rc) fetchRegionsByCountry(rc).catch(() => {});
                  if (rr) fetchAcademiesByRegion(rr).catch(() => {});
                  if (ra) fetchDistrictsByAcademy(ra).catch(() => {});
                  if (rd) fetchInstitutionsByDistrict(rd).catch(() => {});
                  if (ri) fetchDepartmentsByInstitution(ri).catch(() => {});
                }
              });
            }
          })
          .catch(() => {});
      })
      .finally(() => setLoadingTargetUserOrg(false));
  };

  // Close organizational modal
  const closeOrgModal = () => {
    setShowOrgModal(false);
    setSelectedUserForOrg(null);
    setOrgScopeFromCurrentUser(null);
    setOrgScopeNamesFromCurrentUser(null);
    setLoadingTargetUserOrg(false);
    setOrgFormData({
      userId: '',
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    });
    setOrgFormErrors({});
  };

  // هل المستوى مقفل من هيكل المستخدم الحالي (لا يُسمح بتغييره)
  // الـ API قد يعيد أسماء فقط (country_name, region_name, ...) بدون معرفات، لذلك نعتبر المستوى مقفلاً إذا وُجد الاسم
  const levelToNameKey = {
    countryId: 'countryName',
    regionId: 'regionName',
    academyId: 'academyName',
    districtId: 'districtName',
    institutionId: 'institutionName',
    departmentId: 'departmentName'
  };
  const isOrgLevelFixed = (levelKey) => {
    const nameKey = levelToNameKey[levelKey];
    if (nameKey && orgScopeNamesFromCurrentUser) {
      const name = orgScopeNamesFromCurrentUser[nameKey];
      if (name != null && name !== '') return true;
    }
    return !!(orgScopeFromCurrentUser && orgScopeFromCurrentUser[levelKey] !== '' && orgScopeFromCurrentUser[levelKey] != null);
  };

  // Handle country change
  const handleCountryChange = (countryId) => {
    setOrgFormData(prev => ({
      ...prev,
      countryId,
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    
    // Reset dependent data
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    
    // Fetch regions for selected country
    if (countryId) {
      fetchRegionsByCountry(countryId);
    }
  };

  // Handle region change
  const handleRegionChange = (regionId) => {
    setOrgFormData(prev => ({
      ...prev,
      regionId,
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    
    // Reset dependent data
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    
    // Fetch academies for selected region
    if (regionId) {
      fetchAcademiesByRegion(regionId);
    }
  };

  // Handle academy change
  const handleAcademyChange = (academyId) => {
    setOrgFormData(prev => ({
      ...prev,
      academyId,
      districtId: '',
      institutionId: '',
      departmentId: ''
    }));
    
    // Reset dependent data
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    
    // Fetch districts for selected academy
    if (academyId) {
      fetchDistrictsByAcademy(academyId);
    }
  };

  // Handle district change
  const handleDistrictChange = (districtId) => {
    setOrgFormData(prev => ({
      ...prev,
      districtId,
      institutionId: '',
      departmentId: ''
    }));
    
    // Reset dependent data
    setInstitutions([]);
    setDepartments([]);
    
    // Fetch institutions for selected district
    if (districtId) {
      fetchInstitutionsByDistrict(districtId);
    }
  };

  // Handle institution change
  const handleInstitutionChange = (institutionId) => {
    setOrgFormData(prev => ({
      ...prev,
      institutionId,
      departmentId: ''
    }));
    
    // Reset dependent data
    setDepartments([]);
    
    // Fetch departments for selected institution
    if (institutionId) {
      fetchDepartmentsByInstitution(institutionId);
    }
  };

  // اختيار قيمة المستوى: من نطاق المستخدم الحالي (المقفل) إن وُجدت، وإلا من النموذج (الجزء الاختياري للمدرب)
  // لا نستخدم ?? فقط لأن النطاق قد يعيد '' للمستويات الاختيارية فتبقى دون تحديث في قاعدة البيانات
  const scopeOrForm = (levelKey, formData = orgFormData) => {
    const scopeVal = orgScopeFromCurrentUser?.[levelKey];
    const hasScope = scopeVal !== '' && scopeVal != null;
    return hasScope ? scopeVal : (formData[levelKey] ?? '');
  };

  // Assign user to organization — تعيين ناقص، مع دمج هيكل المستخدم الحالي للمستويات المقفلة
  const assignUserToOrganization = async () => {
    setOrgFormErrors({});
    setIsAssigningOrg(true);
    try {
      const requestData = { userId: parseInt(orgFormData.userId) };
      const countryId = scopeOrForm('countryId');
      const regionId = scopeOrForm('regionId');
      const academyId = scopeOrForm('academyId');
      const districtId = scopeOrForm('districtId');
      const institutionId = scopeOrForm('institutionId');
      const departmentId = scopeOrForm('departmentId');
      if (countryId) requestData.countryId = parseInt(countryId);
      if (regionId) requestData.regionId = parseInt(regionId);
      if (academyId) requestData.academyId = parseInt(academyId);
      if (districtId) requestData.districtId = parseInt(districtId);
      if (institutionId) requestData.institutionId = parseInt(institutionId);
      if (departmentId) requestData.departmentId = parseInt(departmentId);
      if (orgFormData.expiresAt) requestData.expiresAt = orgFormData.expiresAt;

      console.log('Assign User to Organization Request Data:', requestData);
      console.log('API Endpoint: /organization/assign-user');
      
      const response = await api.post('/organization/assign-user', requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('users.orgAssignmentSuccess'), { duration: 3000, position: 'top-center' });
        closeOrgModal();
        fetchUsers(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error assigning user to organization:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = t('users.orgAssignmentError');
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAssigningOrg(false);
    }
  };

  // Fetch trainer assignments (departments assigned to trainer)
  const fetchTrainerAssignments = async (trainerId) => {
    if (!trainerId) return;
    setTrainerAssignmentsLoading(true);
    try {
      const response = await api.get(`/organization/trainer-assignments/${trainerId}`);
      const data = response.data;
      let list = [];
      const d = data?.data;
      // API returns { trainer, institution, departments: [{ id, name, assignmentId, ... }] } — one row per department
      if (d && typeof d === 'object' && Array.isArray(d.departments)) {
        const institutionName = d.institution?.name ?? d.institution_name ?? '';
        const institutionId = d.institution?.id ?? d.institution_id;
        list = d.departments.map((dept) => ({
          id: dept.assignmentId ?? dept.id,
          assignment_id: dept.assignmentId ?? dept.id,
          assignmentId: dept.assignmentId,
          departmentId: dept.id,
          department_name: dept.name,
          departmentName: dept.name,
          institution_name: institutionName,
          institutionName,
          institutionId,
          institution_id: institutionId
        }));
      } else {
        const takeArray = (obj, ...keys) => {
          if (!obj) return [];
          for (const k of keys) {
            const v = obj[k];
            if (Array.isArray(v)) return v;
          }
          return [];
        };
        list = takeArray(d, 'assignments', 'trainer_assignments', 'items', 'results')
          .length ? takeArray(d, 'assignments', 'trainer_assignments', 'items', 'results')
          : takeArray(data, 'assignments', 'trainer_assignments', 'items', 'results');
        if (Array.isArray(d) && list.length === 0) list = d;
      }
      setTrainerAssignments(list);
    } catch (error) {
      console.error('Error fetching trainer assignments:', error);
      toast.error(t('users.trainerAssignmentsFetchError'), { duration: 3000, position: 'top-center' });
      setTrainerAssignments([]);
    } finally {
      setTrainerAssignmentsLoading(false);
    }
  };

  // Open trainer departments modal (for users with trainer role)
  const openTrainerDeptsModal = (user) => {
    setSelectedUserForTrainerDepts(user);
    setTrainerDeptFormData({
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentIds: []
    });
    setTrainerDeptFormErrors({});
    setTrainerAssignments([]);
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    setShowTrainerDeptsModal(true);
    fetchOrganizationalData();
    fetchTrainerAssignments(user.id);
  };

  // Close trainer departments modal
  const closeTrainerDeptsModal = () => {
    setShowTrainerDeptsModal(false);
    setSelectedUserForTrainerDepts(null);
    setTrainerAssignments([]);
    setTrainerDeptFormData({
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentIds: []
    });
    setTrainerDeptFormErrors({});
  };

  // Trainer form: cascade handlers (reuse org fetch functions, update trainerDeptFormData)
  const handleTrainerCountryChange = (countryId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      countryId: countryId || '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentIds: []
    }));
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (countryId) fetchRegionsByCountry(countryId);
  };

  const handleTrainerRegionChange = (regionId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      regionId: regionId || '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentIds: []
    }));
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (regionId) fetchAcademiesByRegion(regionId);
  };

  const handleTrainerAcademyChange = (academyId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      academyId: academyId || '',
      districtId: '',
      institutionId: '',
      departmentIds: []
    }));
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
    if (academyId) fetchDistrictsByAcademy(academyId);
  };

  const handleTrainerDistrictChange = (districtId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      districtId: districtId || '',
      institutionId: '',
      departmentIds: []
    }));
    setInstitutions([]);
    setDepartments([]);
    if (districtId) fetchInstitutionsByDistrict(districtId);
  };

  const handleTrainerInstitutionChange = (institutionId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      institutionId: institutionId || '',
      departmentIds: []
    }));
    setDepartments([]);
    if (institutionId) fetchDepartmentsByInstitution(institutionId);
  };

  const toggleTrainerDepartment = (deptId) => {
    setTrainerDeptFormData(prev => ({
      ...prev,
      departmentIds: prev.departmentIds.includes(deptId)
        ? prev.departmentIds.filter(id => id !== deptId)
        : [...prev.departmentIds, deptId]
    }));
  };

  // Assign departments to trainer
  const assignTrainerDepartments = async () => {
    const errors = {};
    if (!trainerDeptFormData.institutionId) errors.institutionId = t('users.institutionRequired');
    if (!trainerDeptFormData.departmentIds?.length) errors.departmentIds = t('users.selectAtLeastOneDepartment');
    setTrainerDeptFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsAssigningTrainerDepts(true);
    try {
      const response = await api.post('/organization/assign-trainer', {
        trainerUserId: parseInt(selectedUserForTrainerDepts.id),
        institutionId: parseInt(trainerDeptFormData.institutionId),
        departmentIds: trainerDeptFormData.departmentIds.map(id => parseInt(id)),
        expiresAt: null
      });
      if (response.data.success) {
        toast.success(response.data.message || t('users.trainerAssignSuccess'), { duration: 3000, position: 'top-center' });
        fetchTrainerAssignments(selectedUserForTrainerDepts.id);
        setTrainerDeptFormData(prev => ({ ...prev, institutionId: '', departmentIds: [] }));
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error assigning trainer departments:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.trainerAssignError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAssigningTrainerDepts(false);
    }
  };

  // Remove trainer assignment
  const removeTrainerAssignment = async (assignmentId) => {
    if (!assignmentId) return;
    setRemovingAssignmentId(assignmentId);
    try {
      const response = await api.delete(`/organization/remove-trainer-assignment/${assignmentId}`);
      if (response.data.success) {
        toast.success(response.data.message || t('users.trainerAssignmentRemovedSuccess'), { duration: 3000, position: 'top-center' });
        fetchTrainerAssignments(selectedUserForTrainerDepts?.id);
      }
    } catch (error) {
      console.error('Error removing trainer assignment:', error);
      const errorMessage = error.response?.data?.message || error.message || t('users.trainerAssignmentRemoveError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setRemovingAssignmentId(null);
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / limit);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchUsers(page, searchTerm, appliedFilters);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary-100 rounded-lg">
                <UsersIcon className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('users.title')}
              </h1>
            </div>
            <p className="text-gray-600">{t('users.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadTemplate}
              title={t('users.downloadTemplate')}
              className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 transition-colors"
            >
              <DownloadIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              title={t('users.importFromExcel')}
              className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              <FileSpreadsheetIcon className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              className="hidden"
            />
            {selectedUserIds.length > 0 && (
              <>
                <button
                  onClick={openBulkStudentModal}
                  title={t('users.bulkCreateStudentProfiles')}
                  className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  <GraduationCapIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={openBulkOrgModal}
                  title={t('users.bulkAssignOrganization')}
                  className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  <BuildingIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={openBulkStatusModal}
                  title={t('users.bulkChangeStatus')}
                  className="p-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                >
                  <UserCheckIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              onClick={openAddModal}
              title={t('users.addUser')}
              className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          {/* صف واحد: بحث + دور + قسم + عناصر في الصفحة + أزرار */}
          <div className={`flex flex-col lg:flex-row gap-4 w-full ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            {/* Search */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('common.search')}
              </label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input
                  type="text"
                  placeholder={t('users.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50/50 hover:bg-white`}
                />
                {isSearching && (
                  <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
                  </div>
                )}
              </div>
            </div>

            {/* Role Filter - auto-applies on change */}
            <div className="lg:w-48">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.role')}
              </label>
              <select
                value={filters.roleId}
                onChange={(e) => {
                  const role = e.target.value;
                  setFilters(prev => ({ ...prev, roleId: role }));
                  const newFilters = { ...filters, roleId: role };
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchUsers(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
                disabled={rolesLoading}
              >
                <option value="">{rolesLoading ? t('users.loadingRoles') : t('users.allRoles')}</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>{t(`roles.${role.name.toUpperCase()}`)}</option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div className="lg:w-56">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.department')}
              </label>
              <select
                value={filters.departmentId}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, departmentId: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchUsers(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="">{t('users.allDepartments')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {getDepartmentTranslation(dept.name, currentLanguage)}
                  </option>
                ))}
              </select>
            </div>

            {/* Items per page */}
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {t('users.perPage')}
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                {LIMIT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button
                type="submit"
                className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                {t('users.applyFilters')}
              </button>
              {(getActiveFiltersCount() > 0 || searchTerm) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  title={t('common.clearFilters')}
                  className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {users.length > 0 && (
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={users.length > 0 && users.every((u) => selectedUserIds.includes(u.id))}
                      onChange={toggleSelectAllOnPage}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                )}
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.user')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.contact')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.role')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.status')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('users.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={users.length > 0 ? 6 : 5} className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-gray-600">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={users.length > 0 ? 6 : 5} className="px-6 py-4 text-center text-gray-500">
                    {t('users.noUsers')}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    {users.length > 0 && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                    )}
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <UserIcon className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className={`${isRTL ? 'mr-4' : 'ml-4'}`}>
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <MailIcon className="h-3 w-3 text-gray-400" />
                          {user.email}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <PhoneIcon className="h-3 w-3 text-gray-400" />
                          {user.phone}
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="space-y-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role, index) => (
                            <div key={index} className="flex items-center">
                              <ShieldIcon className={`h-3 w-3 text-primary-600 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                              <span className="text-xs text-gray-900">
                                {t(`roles.${role.toUpperCase()}`)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">{t('common.notAvailable')}</span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <button
                        onClick={() => toggleUserVerification(user.id, user.is_verified)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_verified === 1 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        } transition-colors`}
                      >
                        {user.is_verified === 1 ? (
                          <CheckCircleIcon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        ) : (
                          <XCircleIcon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        )}
                        {user.is_verified === 1 ? t('users.verified') : t('users.unverified')}
                      </button>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-1' : 'justify-start space-x-1'}`}>
              {/* معاينة بروفايل المستخدم */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Navigating to user profile:', user.id);
                  console.log('Full path:', `/dashboard/users/${user.id}`);
                  console.log('User object:', user);
                  console.log('Current URL before navigation:', window.location.href);
                  console.log('User role context: SUPER_ADMIN');
                  try {
                    navigate(`/dashboard/users/${user.id}`);
                    console.log('Navigation successful');
                    console.log('Current URL after navigation:', window.location.href);
                  } catch (error) {
                    console.error('Navigation error:', error);
                  }
                }}
                className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors hover:bg-blue-50"
                title={t('users.viewProfile')}
                type="button"
                disabled={!user.id}
              >
                <EyeIcon className="h-4 w-4" />
              </button>
                        
                        {/* تعديل بروفايل المستخدم */}
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded"
                          title={t('users.editProfile')}
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        
                        
                        
                        {/* تعيين دور وصلاحيات */}
                        <button
                          onClick={() => openRoleModal(user)}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors"
                          title={t('users.assignRole')}
                        >
                          <SettingsIcon className="h-4 w-4" />
                        </button>

                        {/* تحديث الهيكل التنظيمي — يظهر لمن لديه organization.read (مثلاً المدرب) */}
                        {canReadOrganization && (
                          <button
                            onClick={() => openOrgModal(user)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded transition-colors"
                            title={t('users.updateOrganization')}
                          >
                            <BuildingIcon className="h-4 w-4" />
                          </button>
                        )}

                        {/* توزيع الأقسام على المدرب - يظهر فقط للمستخدمين بدور مدرب */}
                        {user.roles?.includes('trainer') && (
                          <button
                            onClick={() => openTrainerDeptsModal(user)}
                            className="text-teal-600 hover:text-teal-900 p-1 rounded transition-colors"
                            title={t('users.assignTrainerDepartments')}
                          >
                            <LayersIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* إنشاء حساب طالب - يظهر فقط إذا كان الدور طالب وليس لديه حساب طالب بالفعل */}
                        {user.roles?.[0] === 'student' && studentAccountStatus[user.id] === false && (
                          <button
                            onClick={() => openStudentModal(user)}
                            className="text-green-600 hover:text-green-900 p-1 rounded transition-colors"
                            title={t('users.createStudentAccount')}
                          >
                            <GraduationCapIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* عرض أيقونة تحميل أثناء التحقق - يظهر فقط أثناء التحقق */}
                        {user.roles?.[0] === 'student' && checkingStudentStatus[user.id] && (
                          <div className="p-1 rounded">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          </div>
                        )}
                        
                        {/* لا تظهر أي أيقونة إذا كان لديه حساب طالب بالفعل */}
                        
                        {/* تغيير كلمة المرور */}
                        <button
                          onClick={() => openChangePasswordModal(user)}
                          className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                          title={t('users.changePassword')}
                        >
                          <KeyIcon className="h-4 w-4" />
                        </button>
                        
                        {/* حذف المستخدم */}
                        <button
                          onClick={() => deleteUserWithWarning(user.id, `${user.first_name} ${user.last_name}`)}
                          className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                          title={t('users.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('users.previous')}
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('users.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('users.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('users.to')}{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('users.of')}{' '}
                  <span className="font-medium">{total}</span> {t('users.results')}
                </p>
              </div>
              <div>
                <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`} aria-label="Pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && passwordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('users.changePassword')} - {passwordUser.first_name} {passwordUser.last_name}
              </h2>
              <button
                onClick={closePasswordModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={updatingPassword}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-4 py-4 space-y-4">
              {/* New password input + actions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.newPasswordField')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder={t('settings.password.newPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="px-3 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    disabled={updatingPassword}
                  >
                    {t('settings.password.generate')}
                  </button>
                </div>
              </div>

              {/* Copy button + hint under it */}
              <div className="flex flex-col items-start space-y-1">
                <button
                  type="button"
                  onClick={copyNewPasswordToClipboard}
                  className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={!newPassword}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16h8M8 12h8m-6-8h10v12H8zM4 8h10v12H4z" />
                  </svg>
                  {t('settings.password.copy')}
                </button>
                <span className="text-xs text-gray-500 mt-1">
                  {t('users.passwordHint')}
                </span>
                {passwordUser && !canSendPasswordWhatsApp && (
                  <span className="text-xs text-amber-700 block mt-2">
                    {!passwordUser.phone
                      ? t('users.whatsAppPhoneMissingHint')
                      : t('users.whatsAppPhoneRequired')}
                  </span>
                )}
              </div>
            </div>

            <div className="px-4 py-3 border-t flex flex-wrap justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={closePasswordModal}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={updatingPassword}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={savePasswordAndSendWhatsApp}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-95 disabled:opacity-50"
                style={{ backgroundColor: '#25D366' }}
                disabled={updatingPassword || !canSendPasswordWhatsApp}
                title={!canSendPasswordWhatsApp ? t('users.whatsAppPhoneRequired') : undefined}
              >
                <MessageCircleIcon className="h-4 w-4 shrink-0" />
                {updatingPassword ? t('common.updating') : t('users.savePasswordAndWhatsApp')}
              </button>
              <button
                type="button"
                onClick={updateUserPassword}
                className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={updatingPassword}
              >
                {updatingPassword ? t('common.updating') : t('users.changePassword')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('users.addUser')}</h3>
              <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.firstName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.lastName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.username')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.username ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.username && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.password')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.phone')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.phone}
                    onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.dateOfBirth')}
                  </label>
                  <input
                    type="date"
                    value={newUser.dateOfBirth}
                    onChange={(e) => setNewUser(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.gender')}
                  </label>
                  <select
                    value={newUser.gender}
                    onChange={(e) => setNewUser(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="male">{t('users.male')}</option>
                    <option value="female">{t('users.female')}</option>
                    <option value="other">{t('users.other')}</option>
                  </select>
                </div>
              </div>


            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              <button
                onClick={closeAddModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isAdding}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={addUser}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isAdding}
              >
                {isAdding ? t('users.adding') : t('users.addUser')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import from Excel Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('users.importFromExcel')}</h3>
              {!isImporting && (
                <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              )}
            </div>
            <div className="space-y-4">
              {isImporting ? (
                <div className="py-6 text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent mb-4"></div>
                  <p className="text-gray-600">{t('users.importing')}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {importProgress.current} / {importProgress.total}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex gap-4 justify-center py-2">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{importResults.success}</p>
                      <p className="text-sm text-gray-600">{t('users.importSuccessCount')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                      <p className="text-sm text-gray-600">{t('users.importFailedCount')}</p>
                    </div>
                  </div>
                  {importResults.errors?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('users.importErrors')}</p>
                      <ul className="text-xs text-red-600 space-y-1">
                        {importResults.errors.slice(0, 10).map((e, i) => (
                          <li key={i}>Row {e.row}: {e.msg}</li>
                        ))}
                        {importResults.errors.length > 10 && (
                          <li>{t('users.importMoreErrors', { count: importResults.errors.length - 10 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      {t('common.close')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('users.editUser')}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.firstName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, firstName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editFormErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.lastName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, lastName: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      editFormErrors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {editFormErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{editFormErrors.lastName}</p>
                  )}
                </div>
              </div>



              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.phone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingUser.phone}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.phone}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.username')}
                  </label>
                  <input
                    type="text"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.email')}
                  </label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled
                  />
                </div>
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.dateOfBirth')}
                  </label>
                  <input
                    type="date"
                    value={editingUser.dateOfBirth}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.gender')}
                  </label>
                  <select
                    value={editingUser.gender}
                    onChange={(e) => setEditingUser(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="male">{t('users.male')}</option>
                    <option value="female">{t('users.female')}</option>
                    <option value="other">{t('users.other')}</option>
                  </select>
                </div>
              </div>


            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isEditing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={updateUser}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isEditing}
              >
                {isEditing ? t('users.updating') : t('users.update')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('users.assignRoleTo')} {selectedUser.first_name} {selectedUser.last_name}
              </h3>
              <button 
                onClick={() => setShowRoleModal(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Current Roles Display */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3">{t('users.currentRoles')}</h4>
                <div className="space-y-2">
                  {selectedUser.roles && selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((role, index) => {
                      const roleId = roles.find(r => r.name === role)?.id;
                      const isRemoving = removingRoleId === roleId;
                      return (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center">
                            <ShieldIcon className={`w-4 h-4 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="text-sm font-medium text-gray-900">
                              {t(`roles.${role.toUpperCase()}`)}
                            </span>
                          </div>
                          {roleId != null && (
                            <button
                              type="button"
                              onClick={() => removeRoleFromUser(selectedUser.id, roleId)}
                              disabled={isRemoving}
                              className="inline-flex items-center px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                              title={t('users.removeRole')}
                            >
                              <TrashIcon className="w-4 h-4" />
                              {!isRTL && <span className="ml-1">{t('users.removeRole')}</span>}
                              {isRTL && <span className="mr-1">{t('users.removeRole')}</span>}
                            </button>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm">{t('users.noRolesAssigned')}</p>
                  )}
                </div>
              </div>

              {/* Assign New Role */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3">{t('users.assignNewRole')}</h4>
                <div className="flex space-x-3">
                  <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('users.selectRole')}</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {t(`roles.${role.name.toUpperCase()}`)}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={assignRoleToUser}
                    disabled={isAssigningRole || !selectedRoleId}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAssigningRole ? t('common.assigning') : t('users.assignRole')}
                  </button>
                </div>
              </div>
            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Account Creation Modal */}
      {showStudentModal && selectedUserForStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('users.createStudentAccountFor')} {selectedUserForStudent.first_name} {selectedUserForStudent.last_name}
              </h3>
              <button 
                onClick={closeStudentModal} 
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* User Info Display */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('users.userInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t('users.name')}:</span>
                    <span className="ml-2 font-medium">{selectedUserForStudent.first_name} {selectedUserForStudent.last_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('users.email')}:</span>
                    <span className="ml-2 font-medium">{selectedUserForStudent.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('users.username')}:</span>
                    <span className="ml-2 font-medium">@{selectedUserForStudent.username}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('users.phone')}:</span>
                    <span className="ml-2 font-medium">{selectedUserForStudent.phone || t('common.notAvailable')}</span>
                  </div>
                </div>
              </div>

              {/* Student Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.studentNumber')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={studentFormData.studentNumber}
                  onChange={(e) => setStudentFormData(prev => ({ ...prev, studentNumber: e.target.value }))}
                  placeholder="STU2024001"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    studentFormErrors.studentNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {studentFormErrors.studentNumber && (
                  <p className="mt-1 text-sm text-red-600">{studentFormErrors.studentNumber}</p>
                )}
              </div>

              {/* Current Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.currentLevel')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={studentFormData.currentLevelId}
                  onChange={(e) => setStudentFormData(prev => ({ ...prev, currentLevelId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    studentFormErrors.currentLevelId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={learningLevelsLoading}
                >
                  <option value="">
                    {learningLevelsLoading ? t('users.loadingLevels') : t('users.selectLevel')}
                  </option>
                  {learningLevels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.translatedName}
                    </option>
                  ))}
                </select>
                {studentFormErrors.currentLevelId && (
                  <p className="mt-1 text-sm text-red-600">{studentFormErrors.currentLevelId}</p>
                )}
              </div>

              {/* Enrollment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.enrollmentDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={studentFormData.enrollmentDate}
                  onChange={(e) => setStudentFormData(prev => ({ ...prev, enrollmentDate: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    studentFormErrors.enrollmentDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {studentFormErrors.enrollmentDate && (
                  <p className="mt-1 text-sm text-red-600">{studentFormErrors.enrollmentDate}</p>
                )}
              </div>
            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              <button
                onClick={closeStudentModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isCreatingStudent}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={createStudentAccount}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isCreatingStudent}
              >
                {isCreatingStudent ? t('users.creatingStudentAccount') : t('users.createStudentAccount')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Student Profile Creation Modal */}
      {showBulkStudentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('users.bulkCreateStudentProfiles')}
              </h3>
              {!isCreatingBulkStudents && (
                <button
                  onClick={closeBulkStudentModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              )}
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('users.bulkStudentSelectedCount', { count: selectedUserIds.filter((id) => selectableStudentUserIds.includes(id)).length })}
              </p>

              {isCreatingBulkStudents ? (
                <div className="py-4">
                  <p className="text-gray-600">{t('users.bulkStudentCreating')}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {bulkStudentProgress.current} / {bulkStudentProgress.total}
                  </p>
                </div>
              ) : bulkStudentResults.errors?.length > 0 || bulkStudentResults.success > 0 ? (
                <div className="space-y-2">
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">{t('users.bulkStudentSuccessCount', { count: bulkStudentResults.success })}</span>
                    <span className="text-red-600 font-medium">{t('users.bulkStudentFailedCount', { count: bulkStudentResults.failed })}</span>
                  </div>
                  {bulkStudentResults.errors?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('users.importErrors')}</p>
                      <ul className="text-xs text-red-600 space-y-1">
                        {bulkStudentResults.errors.slice(0, 10).map((e, i) => (
                          <li key={i}>{e.name}: {e.msg}</li>
                        ))}
                        {bulkStudentResults.errors.length > 10 && (
                          <li>+{bulkStudentResults.errors.length - 10} {t('users.importMoreErrors', { count: bulkStudentResults.errors.length - 10 })}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.studentNumberPrefix')} <span className="text-gray-400">({t('users.optional')})</span>
                    </label>
                    <input
                      type="text"
                      value={bulkStudentFormData.studentNumberPrefix}
                      onChange={(e) => setBulkStudentFormData((prev) => ({ ...prev, studentNumberPrefix: e.target.value }))}
                      placeholder="STU"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">{t('users.studentNumberPrefixHelp')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.currentLevel')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={bulkStudentFormData.currentLevelId}
                      onChange={(e) => setBulkStudentFormData((prev) => ({ ...prev, currentLevelId: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        bulkStudentFormErrors.currentLevelId ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={learningLevelsLoading}
                    >
                      <option value="">
                        {learningLevelsLoading ? t('users.loadingLevels') : t('users.selectLevel')}
                      </option>
                      {learningLevels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.translatedName}
                        </option>
                      ))}
                    </select>
                    {bulkStudentFormErrors.currentLevelId && (
                      <p className="mt-1 text-sm text-red-600">{bulkStudentFormErrors.currentLevelId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.enrollmentDate')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={bulkStudentFormData.enrollmentDate}
                      onChange={(e) => setBulkStudentFormData((prev) => ({ ...prev, enrollmentDate: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        bulkStudentFormErrors.enrollmentDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {bulkStudentFormErrors.enrollmentDate && (
                      <p className="mt-1 text-sm text-red-600">{bulkStudentFormErrors.enrollmentDate}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              {!isCreatingBulkStudents && (
                <>
                  <button
                    onClick={closeBulkStudentModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    {bulkStudentResults.success > 0 || bulkStudentResults.errors?.length > 0 ? t('common.close') : t('common.cancel')}
                  </button>
                  {(bulkStudentResults.success === 0 && bulkStudentResults.errors?.length === 0) && (
                    <button
                      onClick={createBulkStudentProfiles}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {t('users.bulkCreateStudentProfiles')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Organization Assignment Modal */}
      {showBulkOrgModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('users.bulkAssignOrganization')}</h3>
              {!isAssigningBulkOrg && (
                <button onClick={closeBulkOrgModal} className="text-gray-400 hover:text-gray-500"><span className="text-2xl">&times;</span></button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">{t('users.bulkOrgSelectedCount', { count: selectedUserIds.length })}</p>
            {isAssigningBulkOrg ? (
              <div className="py-4">
                <p className="text-gray-600">{t('users.bulkOrgAssigning')}</p>
                <p className="text-sm text-gray-500 mt-2">{bulkOrgProgress.current} / {bulkOrgProgress.total}</p>
              </div>
            ) : bulkOrgResults.success > 0 || bulkOrgResults.errors?.length > 0 ? (
              <div className="space-y-2">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600 font-medium">{t('users.bulkOrgSuccessCount', { count: bulkOrgResults.success })}</span>
                  <span className="text-red-600 font-medium">{t('users.bulkOrgFailedCount', { count: bulkOrgResults.failed })}</span>
                </div>
                {bulkOrgResults.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">{t('users.importErrors')}</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {bulkOrgResults.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e.name}: {e.msg}</li>
                      ))}
                      {bulkOrgResults.errors.length > 10 && (
                        <li>+{bulkOrgResults.errors.length - 10} {t('users.importMoreErrors', { count: bulkOrgResults.errors.length - 10 })}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ) : loadingBulkOrgScope ? (
              <div className="py-6 flex items-center justify-center gap-2 text-gray-600">
                <span className="inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                {t('users.loadingOrgScope')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.country')} <span className="text-red-500">*</span>
                    {isOrgLevelFixed('countryId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('countryId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getCountryTranslation(orgScopeNamesFromCurrentUser?.countryName, currentLanguage) || orgScopeNamesFromCurrentUser?.countryName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.countryId} onChange={(e) => handleBulkOrgCountryChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.countryId ? 'border-red-500' : 'border-gray-300'}`} disabled={loadingOrgData}>
                        <option value="">{loadingOrgData ? t('users.loadingCountries') : t('users.selectCountry')}</option>
                        {countries.map((c) => <option key={c.id} value={c.id}>{getCountryTranslation(c.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.countryId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.countryId}</p>}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.region')}
                    {isOrgLevelFixed('regionId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('regionId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getRegionTranslation(orgScopeNamesFromCurrentUser?.regionName, currentLanguage) || orgScopeNamesFromCurrentUser?.regionName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.regionId} onChange={(e) => handleBulkOrgRegionChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.regionId ? 'border-red-500' : 'border-gray-300'}`} disabled={!bulkOrgFormData.countryId}>
                        <option value="">{!bulkOrgFormData.countryId ? t('users.selectCountryFirst') : t('users.selectRegion')}</option>
                        {regions.map((r) => <option key={r.id} value={r.id}>{getRegionTranslation(r.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.regionId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.regionId}</p>}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.academy')}
                    {isOrgLevelFixed('academyId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('academyId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getAcademyTranslation(orgScopeNamesFromCurrentUser?.academyName, currentLanguage) || orgScopeNamesFromCurrentUser?.academyName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.academyId} onChange={(e) => handleBulkOrgAcademyChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.academyId ? 'border-red-500' : 'border-gray-300'}`} disabled={!bulkOrgFormData.regionId}>
                        <option value="">{!bulkOrgFormData.regionId ? t('users.selectRegionFirst') : t('users.selectAcademy')}</option>
                        {academies.map((a) => <option key={a.id} value={a.id}>{getAcademyTranslation(a.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.academyId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.academyId}</p>}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.district')}
                    {isOrgLevelFixed('districtId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('districtId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getDistrictTranslation(orgScopeNamesFromCurrentUser?.districtName, currentLanguage) || orgScopeNamesFromCurrentUser?.districtName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.districtId} onChange={(e) => handleBulkOrgDistrictChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.districtId ? 'border-red-500' : 'border-gray-300'}`} disabled={!bulkOrgFormData.academyId}>
                        <option value="">{!bulkOrgFormData.academyId ? t('users.selectAcademyFirst') : t('users.selectDistrict')}</option>
                        {districts.map((d) => <option key={d.id} value={d.id}>{getDistrictTranslation(d.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.districtId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.districtId}</p>}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.institution')}
                    {isOrgLevelFixed('institutionId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('institutionId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getInstitutionTranslation(orgScopeNamesFromCurrentUser?.institutionName, currentLanguage) || orgScopeNamesFromCurrentUser?.institutionName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.institutionId} onChange={(e) => handleBulkOrgInstitutionChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.institutionId ? 'border-red-500' : 'border-gray-300'}`} disabled={!bulkOrgFormData.districtId}>
                        <option value="">{!bulkOrgFormData.districtId ? t('users.selectDistrictFirst') : t('users.selectInstitution')}</option>
                        {institutions.map((i) => <option key={i.id} value={i.id}>{getInstitutionTranslation(i.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.institutionId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.institutionId}</p>}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.department')}
                    {isOrgLevelFixed('departmentId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('departmentId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getDepartmentTranslation(orgScopeNamesFromCurrentUser?.departmentName, currentLanguage) || orgScopeNamesFromCurrentUser?.departmentName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select value={bulkOrgFormData.departmentId} onChange={(e) => handleBulkOrgDepartmentChange(e.target.value)} className={`w-full px-3 py-2 border rounded-lg ${bulkOrgFormErrors.departmentId ? 'border-red-500' : 'border-gray-300'}`} disabled={!bulkOrgFormData.institutionId}>
                        <option value="">{!bulkOrgFormData.institutionId ? t('users.selectInstitutionFirst') : t('users.selectDepartment')}</option>
                        {departments.map((d) => <option key={d.id} value={d.id}>{getDepartmentTranslation(d.name, currentLanguage)}</option>)}
                      </select>
                      {bulkOrgFormErrors.departmentId && <p className="mt-1 text-sm text-red-600">{bulkOrgFormErrors.departmentId}</p>}
                    </>
                  )}
                </div>
              </div>
            )}
            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              {!isAssigningBulkOrg && (
                <>
                  <button onClick={closeBulkOrgModal} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                    {bulkOrgResults.success > 0 || bulkOrgResults.errors?.length > 0 ? t('common.close') : t('common.cancel')}
                  </button>
                  {(bulkOrgResults.success === 0 && bulkOrgResults.errors?.length === 0) && (
                    <button onClick={assignBulkOrganization} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                      {t('users.bulkAssignOrganization')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status (Verification) Change Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('users.bulkChangeStatus')}</h3>
              {!isUpdatingBulkStatus && (
                <button onClick={closeBulkStatusModal} className="text-gray-400 hover:text-gray-500"><span className="text-2xl">&times;</span></button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">{t('users.bulkStatusSelectedCount', { count: selectedUserIds.length })}</p>
            {isUpdatingBulkStatus ? (
              <div className="py-4">
                <p className="text-gray-600">{t('users.bulkStatusUpdating')}</p>
                <p className="text-sm text-gray-500 mt-2">{bulkStatusProgress.current} / {bulkStatusProgress.total}</p>
              </div>
            ) : bulkStatusResults.success > 0 || bulkStatusResults.errors?.length > 0 ? (
              <div className="space-y-2">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600 font-medium">{t('users.bulkStatusSuccessCount', { count: bulkStatusResults.success })}</span>
                  <span className="text-red-600 font-medium">{t('users.bulkStatusFailedCount', { count: bulkStatusResults.failed })}</span>
                </div>
                {bulkStatusResults.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">{t('users.importErrors')}</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {bulkStatusResults.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e.name}: {e.msg}</li>
                      ))}
                      {bulkStatusResults.errors.length > 10 && (
                        <li>+{bulkStatusResults.errors.length - 10} {t('users.importMoreErrors', { count: bulkStatusResults.errors.length - 10 })}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.bulkStatusNewValue')}</label>
                <div className="flex gap-4">
                  <label className="inline-flex items-center">
                    <input type="radio" checked={bulkStatusValue === true} onChange={() => setBulkStatusValue(true)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    <span className="ml-2">{t('users.verified')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input type="radio" checked={bulkStatusValue === false} onChange={() => setBulkStatusValue(false)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    <span className="ml-2">{t('users.unverified')}</span>
                  </label>
                </div>
              </div>
            )}
            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              {!isUpdatingBulkStatus && (
                <>
                  <button onClick={closeBulkStatusModal} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                    {bulkStatusResults.success > 0 || bulkStatusResults.errors?.length > 0 ? t('common.close') : t('common.cancel')}
                  </button>
                  {(bulkStatusResults.success === 0 && bulkStatusResults.errors?.length === 0) && (
                    <button onClick={updateBulkStatus} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                      {t('users.bulkChangeStatus')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Organizational Assignment Modal */}
      {showOrgModal && selectedUserForOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('users.updateOrganizationFor')} {selectedUserForOrg.first_name} {selectedUserForOrg.last_name}
              </h3>
              <button 
                onClick={closeOrgModal} 
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              {/* User Info Display */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('users.userInfo')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t('users.name')}:</span>
                    <span className="ml-2 font-medium">{selectedUserForOrg.first_name} {selectedUserForOrg.last_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('users.email')}:</span>
                    <span className="ml-2 font-medium">{selectedUserForOrg.email}</span>
                  </div>
                </div>
              </div>

              {loadingTargetUserOrg && (
                <p className="text-sm text-gray-500 flex items-center">
                  <span className="inline-block w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2" />
                  {t('users.loadingUserOrg')}
                </p>
              )}

              {/* Organizational Structure */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.country')} <span className="text-red-500">*</span>
                    {isOrgLevelFixed('countryId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('countryId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getCountryTranslation(orgScopeNamesFromCurrentUser?.countryName, currentLanguage) || orgScopeNamesFromCurrentUser?.countryName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.countryId}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.countryId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loadingOrgData}
                      >
                        <option value="">
                          {loadingOrgData ? t('users.loadingCountries') : t('users.selectCountry')}
                        </option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {getCountryTranslation(country.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.countryId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.countryId}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Region */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.region')}
                    {isOrgLevelFixed('regionId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('regionId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getRegionTranslation(orgScopeNamesFromCurrentUser?.regionName, currentLanguage) || orgScopeNamesFromCurrentUser?.regionName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.regionId}
                        onChange={(e) => handleRegionChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.regionId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!orgFormData.countryId}
                      >
                        <option value="">
                          {!orgFormData.countryId ? t('users.selectCountryFirst') : t('users.selectRegion')}
                        </option>
                        {regions.map((region) => (
                          <option key={region.id} value={region.id}>
                            {getRegionTranslation(region.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.regionId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.regionId}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Academy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.academy')}
                    {isOrgLevelFixed('academyId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('academyId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getAcademyTranslation(orgScopeNamesFromCurrentUser?.academyName, currentLanguage) || orgScopeNamesFromCurrentUser?.academyName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.academyId}
                        onChange={(e) => handleAcademyChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.academyId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!orgFormData.regionId}
                      >
                        <option value="">
                          {!orgFormData.regionId ? t('users.selectRegionFirst') : t('users.selectAcademy')}
                        </option>
                        {academies.map((academy) => (
                          <option key={academy.id} value={academy.id}>
                            {getAcademyTranslation(academy.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.academyId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.academyId}</p>
                      )}
                    </>
                  )}
                </div>

                {/* District */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.district')}
                    {isOrgLevelFixed('districtId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('districtId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getDistrictTranslation(orgScopeNamesFromCurrentUser?.districtName, currentLanguage) || orgScopeNamesFromCurrentUser?.districtName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.districtId}
                        onChange={(e) => handleDistrictChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.districtId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!orgFormData.academyId}
                      >
                        <option value="">
                          {!orgFormData.academyId ? t('users.selectAcademyFirst') : t('users.selectDistrict')}
                        </option>
                        {districts.map((district) => (
                          <option key={district.id} value={district.id}>
                            {getDistrictTranslation(district.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.districtId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.districtId}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Institution */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.institution')}
                    {isOrgLevelFixed('institutionId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('institutionId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getInstitutionTranslation(orgScopeNamesFromCurrentUser?.institutionName, currentLanguage) || orgScopeNamesFromCurrentUser?.institutionName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.institutionId}
                        onChange={(e) => handleInstitutionChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.institutionId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!orgFormData.districtId}
                      >
                        <option value="">
                          {!orgFormData.districtId ? t('users.selectDistrictFirst') : t('users.selectInstitution')}
                        </option>
                        {institutions.map((institution) => (
                          <option key={institution.id} value={institution.id}>
                            {getInstitutionTranslation(institution.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.institutionId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.institutionId}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('users.department')}
                    {isOrgLevelFixed('departmentId') && (
                      <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                        <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                      </span>
                    )}
                  </label>
                  {isOrgLevelFixed('departmentId') ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                      {getDepartmentTranslation(orgScopeNamesFromCurrentUser?.departmentName, currentLanguage) || orgScopeNamesFromCurrentUser?.departmentName || t('common.notAvailable')}
                    </div>
                  ) : (
                    <>
                      <select
                        value={orgFormData.departmentId}
                        onChange={(e) => setOrgFormData(prev => ({ ...prev, departmentId: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          orgFormErrors.departmentId ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={!orgFormData.institutionId}
                      >
                        <option value="">
                          {!orgFormData.institutionId ? t('users.selectInstitutionFirst') : t('users.selectDepartment')}
                        </option>
                        {departments.map((department) => (
                          <option key={department.id} value={department.id}>
                            {getDepartmentTranslation(department.name, currentLanguage)}
                          </option>
                        ))}
                      </select>
                      {orgFormErrors.departmentId && (
                        <p className="mt-1 text-sm text-red-600">{orgFormErrors.departmentId}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={`mt-6 flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
              <button
                onClick={closeOrgModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isAssigningOrg}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={assignUserToOrganization}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                disabled={isAssigningOrg || loadingOrgData || loadingTargetUserOrg}
              >
                {isAssigningOrg ? t('users.assigning') : t('users.assignOrganization')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trainer departments assignment modal */}
      {showTrainerDeptsModal && selectedUserForTrainerDepts && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('users.assignTrainerDepartmentsFor')} {selectedUserForTrainerDepts.first_name} {selectedUserForTrainerDepts.last_name}
              </h3>
              <button
                onClick={closeTrainerDeptsModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {/* Current assignments */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-3">{t('users.trainerCurrentAssignments')}</h4>
              {trainerAssignmentsLoading ? (
                <p className="text-gray-500 text-sm">{t('common.loading')}</p>
              ) : trainerAssignments.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {trainerAssignments.map((row) => {
                    const assignmentId = row.assignmentId ?? row.id ?? row.assignment_id;
                    const isRemoving = removingAssignmentId === assignmentId;
                    const institutionLabel = getInstitutionTranslation(row.institution_name || row.institutionName, currentLanguage) || t('users.institution');
                    const departmentLabel = getDepartmentTranslation(row.department_name || row.departmentName, currentLanguage);
                    return (
                      <div key={`${row.departmentId ?? row.id}-${assignmentId}-${row.department_name}`} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center min-w-0">
                          <LayersIcon className={`w-4 h-4 text-teal-600 flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-sm text-gray-900 truncate">
                            <span className="font-medium text-gray-700">{institutionLabel}</span>
                            {departmentLabel && (
                              <span className="text-gray-500"> — {departmentLabel}</span>
                            )}
                          </span>
                        </div>
                        {assignmentId != null && (
                          <button
                            type="button"
                            onClick={() => removeTrainerAssignment(assignmentId)}
                            disabled={isRemoving}
                            className="inline-flex items-center flex-shrink-0 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title={t('users.removeAssignment')}
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span className={isRTL ? 'mr-1' : 'ml-1'}>{t('users.removeAssignment')}</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">{t('users.noTrainerAssignments')}</p>
              )}
            </div>

            {/* Add new assignment: cascade to institution + multi-select departments */}
            <div className="border-t pt-6">
              <h4 className="text-md font-medium text-gray-700 mb-3">{t('users.addTrainerAssignment')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.country')}</label>
                  <select
                    value={trainerDeptFormData.countryId}
                    onChange={(e) => handleTrainerCountryChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">{t('users.selectCountry')}</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>{getCountryTranslation(c.name, currentLanguage)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.region')}</label>
                  <select
                    value={trainerDeptFormData.regionId}
                    onChange={(e) => handleTrainerRegionChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    disabled={!trainerDeptFormData.countryId}
                  >
                    <option value="">{!trainerDeptFormData.countryId ? t('users.selectCountryFirst') : t('users.selectRegion')}</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>{getRegionTranslation(r.name, currentLanguage)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.academy')}</label>
                  <select
                    value={trainerDeptFormData.academyId}
                    onChange={(e) => handleTrainerAcademyChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    disabled={!trainerDeptFormData.regionId}
                  >
                    <option value="">{!trainerDeptFormData.regionId ? t('users.selectRegionFirst') : t('users.selectAcademy')}</option>
                    {academies.map((a) => (
                      <option key={a.id} value={a.id}>{getAcademyTranslation(a.name, currentLanguage)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.district')}</label>
                  <select
                    value={trainerDeptFormData.districtId}
                    onChange={(e) => handleTrainerDistrictChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    disabled={!trainerDeptFormData.academyId}
                  >
                    <option value="">{!trainerDeptFormData.academyId ? t('users.selectAcademyFirst') : t('users.selectDistrict')}</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>{getDistrictTranslation(d.name, currentLanguage)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('users.institution')} <span className="text-red-500">*</span></label>
                  <select
                    value={trainerDeptFormData.institutionId}
                    onChange={(e) => handleTrainerInstitutionChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${trainerDeptFormErrors.institutionId ? 'border-red-500' : 'border-gray-300'}`}
                    disabled={!trainerDeptFormData.districtId}
                  >
                    <option value="">{!trainerDeptFormData.districtId ? t('users.selectDistrictFirst') : t('users.selectInstitution')}</option>
                    {institutions.map((i) => (
                      <option key={i.id} value={i.id}>{getInstitutionTranslation(i.name, currentLanguage)}</option>
                    ))}
                  </select>
                  {trainerDeptFormErrors.institutionId && <p className="mt-1 text-sm text-red-600">{trainerDeptFormErrors.institutionId}</p>}
                </div>
              </div>

              {trainerDeptFormData.institutionId && departments.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('users.departments')} <span className="text-red-500">*</span></label>
                  <div className="flex flex-wrap gap-2">
                    {departments.map((dept) => (
                      <label key={dept.id} className="inline-flex items-center px-3 py-1.5 bg-gray-50 border rounded-lg cursor-pointer hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={trainerDeptFormData.departmentIds.includes(dept.id)}
                          onChange={() => toggleTrainerDepartment(dept.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className={`text-sm ${isRTL ? 'mr-2' : 'ml-2'}`}>{getDepartmentTranslation(dept.name, currentLanguage)}</span>
                      </label>
                    ))}
                  </div>
                  {trainerDeptFormErrors.departmentIds && <p className="mt-1 text-sm text-red-600">{trainerDeptFormErrors.departmentIds}</p>}
                </div>
              )}

              <div className={`flex ${isRTL ? 'justify-start space-x-reverse space-x-3' : 'justify-end space-x-3'}`}>
                <button
                  onClick={closeTrainerDeptsModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={assignTrainerDepartments}
                  disabled={isAssigningTrainerDepts || !trainerDeptFormData.institutionId || !trainerDeptFormData.departmentIds?.length}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {isAssigningTrainerDepts ? t('users.assigning') : t('users.assignTrainerDepartments')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
